/**
 * What each role sees.
 *
 * One function per view, each returning a string of HTML and then wiring its
 * own controls. That is the right shape for something this size: the page is a few hundred nodes, redrawing it costs
 * nothing a person can perceive, and there is no second description of the DOM
 * to keep in step with the first.
 */

import {
  CAMPAIGN_STATUSES,
  COMPLAINT_CATEGORIES,
  EXPENSE_CATEGORIES,
  compareUnitIds,
  advertisementInput,
  isReference,
  movesFrom,
  refusalReasonOf,
  formatPeriod,
  offers,
  AUTO_REMINDER_DELAY_DAYS,
  addPeriods,
  periodOf,
  reminderDateOf,
  comparePeriods,
  makePeriod,
  splitPeriod,
  ETHIOPIC_MONTHS_AM,
  ETHIOPIC_MONTHS_LATIN,
  CONTRACTOR_TRADES,
  REMINDER_LIMIT,
  REMINDER_NOTE_LIMIT,
  messageFor,
  tradeForRepair,
  chapaMoney,
  reachesBankBy,
  type ChapaReceiptDetail,
  type HeldStatus,
  heldFor,
  periodsSettledBy,
  planPayment,
} from "@liyu/core";
import { esc, hold, pressed, save, showReceipt, toast } from "./dom";
import * as data from "./data";
import { behind, followUnit, map, store, totals } from "./store";
import type {
  ContractorTrade,
  ComplaintCategory,
  ComplaintStatus,
  ExpenseCategory,
  Period,
  PeriodSummary,
} from "@liyu/core";
import type { Session } from "./session";
import {
  bar,
  card,
  empty,
  field,
  gold,
  grid,
  money,
  named,
  on,
  percent,
  pill,
  row,
  rowList,
  sectionTitle,
  stat,
  table,
} from "./pieces";
import { excelFileName, statement, toExcel } from "./statement";
import { language, other, otherLanguage, t } from "./strings";
import type { Key } from "./strings";
import { analyticsView, metricCards, metricFromRoute, wireMetrics } from "./analytics";
import { chatView } from "./chat";

export interface View {
  title: string;
  sub: string;
  html: string;
  wire?: (root: HTMLElement, go: (view: string) => void) => void;
}

/** A trade, in the page's language. */
function tradeName(trade: ContractorTrade): string {
  return t(`trade.${trade}`);
}

/**
 * What the contractors list is narrowed to: a trade and a name. Kept for the
 * session, like the units list's filters.
 */
const contractorFilter = { trade: "all", search: "" };

function contractorMatches(ad: data.Advertisement, trade: string, search: string): boolean {
  const wanted = search.trim().toLowerCase();
  return (
    (trade === "all" || ad.trade === trade) &&
    (wanted === "" || ad.businessName.toLowerCase().includes(wanted))
  );
}

/** The trades actually listed, so the filter never offers one with nobody in it. */
function listedTrades(): ContractorTrade[] {
  return CONTRACTOR_TRADES.filter((trade) => store.advertisements.some((ad) => ad.trade === trade));
}

/**
 * The trade and name filters above a contractor list, and the rows under them.
 * Filtering hides rows already drawn, so typing a name keeps the box's focus;
 * `extra` is whatever each row ends with, such as the committee's buttons.
 */
function contractorDirectory(extra: (ad: data.Advertisement) => string): string {
  if (store.advertisements.length === 0) {
    return empty(t("advertisements.none"));
  }
  const chosenTrade = contractorFilter.trade;
  const rows = store.advertisements
    .slice()
    .sort((a, b) => a.businessName.localeCompare(b.businessName))
    .map((ad) => {
      const hidden = contractorMatches(ad, chosenTrade, contractorFilter.search) ? "" : " hidden";
      return (
        `<div class="row contractor-row" data-trade="${esc(ad.trade)}" data-name="${esc(
          ad.businessName.toLowerCase(),
        )}"${hidden}><div class="left"><strong>${esc(ad.businessName)}</strong>` +
        `<span>${esc(tradeName(ad.trade))} · ${esc(ad.description)} · ${esc(ad.phone)}</span></div>` +
        extra(ad) +
        `</div>`
      );
    });
  const shown = store.advertisements.filter((ad) =>
    contractorMatches(ad, chosenTrade, contractorFilter.search),
  ).length;
  return (
    `<div class="unit-filters contractor-filters">` +
    `<div><label for="contractorTrade">${esc(t("ads.trade"))}</label><select class="field" id="contractorTrade">` +
    [
      ["all", t("contractors.allTrades")] as const,
      ...listedTrades().map((trade) => [trade, tradeName(trade)] as const),
    ]
      .map(
        ([key, label]) =>
          `<option value="${esc(key)}"${key === chosenTrade ? " selected" : ""}>${esc(label)}</option>`,
      )
      .join("") +
    `</select></div>` +
    `<div><label for="contractorSearch">${esc(t("contractors.search"))}</label><input class="field" id="contractorSearch" type="search" autocomplete="off" placeholder="${esc(
      t("contractors.searchHint"),
    )}" value="${esc(contractorFilter.search)}" /></div>` +
    `</div>` +
    `<div class="unit-count"><span id="contractorCount" aria-live="polite">${esc(
      t("contractors.showing", { shown, total: store.advertisements.length }),
    )}</span></div>` +
    card(`<div class="row-list">${rows.join("")}</div>`) +
    `<p class="empty" id="contractorNone"${shown === 0 ? "" : " hidden"}>${esc(t("contractors.none"))}</p>`
  );
}

/** Wires the filters `contractorDirectory` drew. */
function wireContractorDirectory(root: HTMLElement): void {
  const apply = (): void => {
    let shown = 0;
    for (const row of root.querySelectorAll<HTMLElement>(".contractor-row")) {
      const trade = row.dataset.trade ?? "";
      const name = row.dataset.name ?? "";
      const match =
        (contractorFilter.trade === "all" || trade === contractorFilter.trade) &&
        (contractorFilter.search.trim() === "" ||
          name.includes(contractorFilter.search.trim().toLowerCase()));
      row.hidden = !match;
      if (match) shown += 1;
    }
    const count = root.querySelector("#contractorCount");
    if (count) {
      count.textContent = t("contractors.showing", {
        shown,
        total: store.advertisements.length,
      });
    }
    root.querySelector<HTMLElement>("#contractorNone")?.toggleAttribute("hidden", shown > 0);
  };
  root.querySelector<HTMLSelectElement>("#contractorTrade")?.addEventListener("change", (event) => {
    contractorFilter.trade = (event.target as HTMLSelectElement).value;
    apply();
  });
  root.querySelector<HTMLInputElement>("#contractorSearch")?.addEventListener("input", (event) => {
    contractorFilter.search = (event.target as HTMLInputElement).value;
    apply();
  });
}

/** Contractors the committee lists: who, what they do, and how to reach them. */
function advertisementList(): string {
  return contractorDirectory(
    (ad) => `<a class="btn btn-ghost btn-sm" href="tel:${esc(ad.phone)}">${esc(ad.phone)}</a>`,
  );
}

/** A receipt picker, for the photograph of a transfer. */
function receiptField(id: string): string {
  return `<div><label for="${esc(id)}">${esc(t("resident.receipt"))}</label><input class="field" id="${esc(
    id,
  )}" type="file" accept="image/jpeg,image/png,image/webp,image/heic" /><p class="hint">${esc(
    t("resident.receiptHint"),
  )}</p></div>`;
}

/**
 * The transfer's reference and photograph, both required, as on the phone: an
 * accountant cannot check a transfer without them. Says what is missing and
 * returns null, or returns both.
 */
function evidenceFrom(
  root: HTMLElement,
  referenceId: string,
  receiptId: string,
): { reference: string; file: File } | null {
  const reference = root.querySelector<HTMLInputElement>(`#${referenceId}`)?.value.trim() ?? "";
  if (!isReference(reference)) {
    toast(t("resident.needReference"));
    root.querySelector<HTMLInputElement>(`#${referenceId}`)?.focus();
    return null;
  }
  const file = root.querySelector<HTMLInputElement>(`#${receiptId}`)?.files?.[0];
  if (!file) {
    toast(t("resident.needReceipt"));
    return null;
  }
  return { reference, file };
}

/** The category chosen on the form, which only ever offers the contract's own. */
function expenseCategory(value: string | undefined): ExpenseCategory {
  return EXPENSE_CATEGORIES.find((category) => category === value) ?? "other";
}

/** A repair is settled once the resident has confirmed it or it is closed. */
const SETTLED = new Set(["confirmed", "closed"]);

function dismissedReminders(): Set<string> {
  try {
    const raw = window.localStorage.getItem("liyu-dismissed-reminders");
    const ids = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(
      Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [],
    );
  } catch {
    return new Set();
  }
}

function dismissReminder(id: string): void {
  const ids = dismissedReminders();
  ids.add(id);
  window.localStorage.setItem("liyu-dismissed-reminders", JSON.stringify([...ids].slice(-50)));
}

/**
 * A month, written the way the phone writes it.
 *
 * `EC2018-12` is an identifier, not a date. It is the right thing to sort and
 * match on and the wrong thing to show a resident, who is being asked whether
 * they have paid for Nehase.
 */
function month(period: Period): string {
  return formatPeriod(period, language() === "am" ? "am" : "latin");
}

/** The chosen repair category, or the one everything else falls under. */
function category(chosen: string | undefined): ComplaintCategory {
  return COMPLAINT_CATEGORIES.find((known) => known === chosen) ?? "other";
}

/* ------------------------------------------------------------ resident -- */

export function resident(view: string, session: Session): View {
  const unitId = session.unitId ?? "";
  const owed = store.months
    .filter((m) => m.unitId === unitId)
    .reduce((sum, m) => sum + m.summary.balance, 0);
  const pending = store.mine.filter((p) => p.status === "submitted");
  const pendingAmount = pending.reduce((sum, p) => sum + p.amount, 0);
  const reminder = store.reminders.find((item) => !dismissedReminders().has(item.id));
  const mine = store.complaints.filter(
    (c) => (c.unitId === unitId || c.unitId === null) && !SETTLED.has(c.status),
  );

  if (view === "pay") {
    return residentPay(session, owed);
  }
  if (view === "payments") {
    return residentHistory();
  }
  if (view === "notifications") {
    return inboxList();
  }
  if (view.startsWith("notification:")) {
    return inboxItem(session, view.slice("notification:".length));
  }
  if (view === "repairs") {
    return residentRepairs(session);
  }
  if (view === "notices") {
    return { title: t("nav.notices"), sub: "", html: noticeList() };
  }
  if (view === "give") {
    return residentGive(session);
  }
  if (view === "chat") {
    return chatView(session);
  }

  const months = store.months
    .filter((m) => m.unitId === unitId)
    .sort((a, b) => b.summary.period.localeCompare(a.summary.period));

  const owing = months
    .filter((m) => m.summary.balance > 0)
    .map((m) => m.summary)
    .sort((a, b) => comparePeriods(a.period, b.period));
  const latest = [...store.mine].sort(
    (a, b) => (b.submittedAt?.getTime() ?? 0) - (a.submittedAt?.getTime() ?? 0),
  )[0];
  const notice = store.notices[0];
  const payLabel: [Key, Record<string, string>] =
    owed > 0 ? ["home.pay", { amount: money(owed) }] : ["home.payFees", {}];

  return {
    title: t("nav.home"),
    sub: unitId,
    html:
      (reminder
        ? `<div class="payment-reminder" role="status"><div><strong>${esc(t("resident.paymentReminder"))}</strong><p>${esc(t("resident.paymentReminderBody", { amount: money(reminder.amount), months: reminder.months }))}</p></div><button type="button" class="btn btn-ghost btn-sm" id="dismissReminder">${esc(t("resident.dismissReminder"))}</button></div>`
        : "") +
      `<section class="slip" aria-labelledby="oweLabel"><div class="slip-main">` +
      `<div class="slip-meta"><span>${esc(t("home.receiptNo"))} ${esc(unitId)}</span><span>${esc(month(store.period))}</span></div>` +
      `<h2 class="slip-label" id="oweLabel">${esc(t("resident.owe"))}${sayOther("resident.owe")}</h2>` +
      `<div class="slip-amount">${esc(money(owed))}</div>` +
      (owed > 0
        ? `<p class="slip-owed">${esc(t("home.unpaid", { months: owing.map((m) => month(m.period)).join(", ") }))}</p>`
        : `<p class="slip-settled">${esc(t("resident.oweNone"))}</p>`) +
      (pendingAmount > 0
        ? `<p class="slip-note">${esc(t("home.withAccountant", { amount: money(pendingAmount) }))}</p>`
        : "") +
      `</div><div class="slip-stub">` +
      `<button type="button" class="slip-pay" id="goPay"><span class="main-line">${esc(
        t(...payLabel),
      )}</span><span class="other-line" lang="${otherLanguage()}">${esc(other(...payLabel))}</span></button>` +
      (store.chapa ? `<span class="slip-methods">${esc(t("home.methods"))}</span>` : "") +
      `</div></section>` +
      yearOf(months.map((m) => m.summary)) +
      `<section class="ledger-links">` +
      `<button type="button" class="ledger-link" id="goRepairs"><strong>${esc(
        t("home.repairs", { count: mine.length }),
      )}</strong><span>${esc(t("resident.openRepairsSub"))}</span></button>` +
      `<button type="button" class="ledger-link" id="goNotices"><strong>${esc(
        notice ? notice.title : t("home.notice"),
      )}</strong><span>${esc(notice ? on(notice.postedAt) : t("home.noNotices"))}</span></button>` +
      `<button type="button" class="ledger-link" id="goHistory"><strong>${esc(
        t("home.history"),
      )}</strong><span>${esc(
        latest
          ? `${money(latest.amount)} · ${on(latest.submittedAt)} · ${paymentState(latest.status)}`
          : t("home.historySub"),
      )}</span></button>` +
      `</section>`,
    wire: (root, go) => {
      root.querySelector("#goPay")?.addEventListener("click", () => go("pay"));
      root.querySelector("#goRepairs")?.addEventListener("click", () => go("repairs"));
      root.querySelector("#goHistory")?.addEventListener("click", () => go("payments"));
      root.querySelector<HTMLButtonElement>("#dismissReminder")?.addEventListener("click", () => {
        if (reminder) {
          dismissReminder(reminder.id);
          root.querySelector(".payment-reminder")?.remove();
        }
      });
      root.querySelector("#goNotices")?.addEventListener("click", () => go("notices"));
    },
  };
}

/** The same words in the other language, small, after a heading. */
function sayOther(key: Key, values: Record<string, string | number> = {}): string {
  return `<span class="say-other" lang="${otherLanguage()}">${esc(other(key, values))}</span>`;
}

/** A month's name in the page's script, and in the other one. */
function monthNames(monthOfYear: number): [string, string] {
  const am = ETHIOPIC_MONTHS_AM[monthOfYear - 1] ?? "";
  const latin = ETHIOPIC_MONTHS_LATIN[monthOfYear - 1] ?? "";
  return language() === "am" ? [am, latin] : [latin, am];
}

/** The state of a month as the committee would mark it: its stamp, in both languages. */
const STAMP: Record<string, Key> = {
  verified: "state.verified",
  unverified: "state.unverified",
  unpaid: "state.unpaid",
  partial: "state.partial",
  inactive: "state.inactive",
};

function stamp(state: string): string {
  const key = STAMP[state] ?? "state.inactive";
  return `<span class="stamp stamp-${esc(STAMP[state] ? state : "inactive")}"><span class="stamp-main">${esc(
    t(key),
  )}</span><span class="stamp-other" lang="${otherLanguage()}">${esc(other(key))}</span></span>`;
}

/** What a payment the resident sent became, in a word. */
function paymentState(status: data.Payment["status"]): string {
  if (status === "verified") return t("state.verified");
  if (status === "rejected") return named("action", "payment.rejected");
  return t("state.unverified");
}

/**
 * The year the current month is in, as the committee bills it: twelve months
 * of thirty days and Pagumen, which is never billed. A month already raised
 * carries its stamp; one still ahead says so; earlier years' months that still
 * owe are listed under it, because a debt does not end with the year.
 */
function yearOf(summaries: PeriodSummary[]): string {
  const { year } = splitPeriod(store.period);
  const tiles: string[] = [];
  for (let monthOfYear = 1; monthOfYear <= 12; monthOfYear += 1) {
    const period = makePeriod(year, monthOfYear);
    const summary = summaries.find((m) => m.period === period);
    const ahead = comparePeriods(period, store.period) > 0;
    const [name, otherName] = monthNames(monthOfYear);
    const classes = [
      "month-tile",
      period === store.period ? "current" : "",
      !summary && ahead ? "future" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const detail = summary
      ? `${otherName} · ${money(summary.charged)}${period === store.period ? ` · ${t("home.now")}` : ""}`
      : `${otherName} · ${ahead ? t("home.notDue") : t("state.inactive")}`;
    tiles.push(
      `<div class="${classes}"><div class="month-name">${esc(name)}</div><div class="month-sub">${esc(
        detail,
      )}</div>${summary ? stamp(summary.state) : ""}</div>`,
    );
  }
  const [pagumen, pagumenOther] = monthNames(13);
  tiles.push(
    `<div class="month-tile pagumen"><div class="month-name">${esc(pagumen)}</div><div class="month-sub">${esc(
      `${pagumenOther} · ${t("state.inactive")}`,
    )}</div></div>`,
  );
  const earlier = summaries
    .filter((m) => m.balance > 0 && splitPeriod(m.period).year < year)
    .sort((a, b) => comparePeriods(a.period, b.period));
  return (
    `<div class="year-head"><h2>${esc(t("home.year", { year }))}</h2>${sayOther("home.yearSub")}</div>` +
    `<div class="year-grid">${tiles.join("")}</div>` +
    (earlier.length === 0
      ? ""
      : sectionTitle(t("home.earlier")) +
        card(rowList(earlier.map((m) => row(month(m.period), money(m.balance), pill(m.state))))))
  );
}

/**
 * Which months an amount settles, oldest first, as the verification will. A
 * month part covered counts as reached; what is left after every owed month
 * is held for the accountant rather than credited ahead.
 */
function coverage(amount: number, owing: PeriodSummary[], ahead: Period[] = []): string {
  if (!Number.isSafeInteger(amount) || amount <= 0) return "";
  if (ahead.length > 0) return ahead.map(month).join(", ");
  if (owing.length === 0) return t("pay.nothingOwed");
  let left = amount;
  const reached: string[] = [];
  for (const m of owing) {
    if (left <= 0) break;
    reached.push(month(m.period));
    left -= m.balance;
  }
  return reached.join(", ") + (left > 0 ? ` ${t("pay.held", { amount: money(left) })}` : "");
}

/** How far ahead the pay screen offers, as a few round choices rather than thirteen. */
const AHEAD_CHOICES = [1, 3, 6, 12] as const;

/** What the accountant did with money past what was owed, said to the resident. */
const EXTRA_SAID: Record<HeldStatus, Key> = {
  unresolved: "history.extra.unresolved",
  refunded: "history.extra.refunded",
  waived: "history.extra.waived",
  extended: "history.extra.extended",
};

/** A payment's state as the pill that already says it elsewhere on the page. */
const PAYMENT_PILL: Record<data.Payment["status"], string> = {
  submitted: "unverified",
  verified: "verified",
  rejected: "unpaid",
};

/**
 * Every payment this unit sent, newest first, and what became of each.
 *
 * The same story the phone tells: waiting says what it waits on, verified names
 * the months it settled from the entries it wrote, turned down shows the
 * accountant's reason as written. Money past what was owed is said beside the
 * payment that brought it, with what the accountant did with it, because until
 * now only the accountant's queue showed it.
 */
function residentHistory(): View {
  const rows = [...store.mine].sort(
    (a, b) => (b.submittedAt?.getTime() ?? 0) - (a.submittedAt?.getTime() ?? 0),
  );
  const body = rows.length === 0 ? empty(t("history.none")) : rows.map(historyItem).join("");
  return {
    title: t("history.title"),
    sub: t("history.lead"),
    html: body,
    wire: (root, go) => {
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-receipt]")) {
        button.addEventListener("click", () => {
          const original = button.textContent ?? "";
          button.disabled = true;
          button.textContent = t("units.receiptLoading");
          void data
            .receiptUrl(button.dataset.receipt ?? "")
            .then((url) => showReceipt(url))
            .catch((cause: Error) => {
              console.error("Opening a receipt from the payment history failed", cause);
              toast(t("units.receiptUnavailable"));
            })
            .finally(() => {
              button.disabled = false;
              button.textContent = original;
            });
        });
      }
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-send-again]")) {
        button.addEventListener("click", () => go("pay"));
      }
    },
  };
}

/** One payment, told as what happened to it. */
function historyItem(payment: data.Payment): string {
  const verified = payment.status === "verified";
  const covered = verified ? periodsSettledBy(store.entries, payment.id) : [];
  const extra = verified ? heldFor(store.extras, payment.id) : null;
  const campaign = payment.campaignId
    ? store.campaigns.find((c) => c.id === payment.campaignId)
    : undefined;
  const meta = [
    on(payment.submittedAt),
    payment.method === "chapa" ? t("history.viaChapa") : t("history.viaBank"),
    payment.reference ?? "",
  ]
    .filter(Boolean)
    .join(" · ");
  const lines = [
    campaign ? t("history.towards", { title: campaign.title }) : "",
    payment.status === "submitted" ? t("history.waiting") : "",
    covered.length > 0 ? t("history.covered", { months: covered.map(month).join(", ") }) : "",
    extra
      ? t(EXTRA_SAID[extra.status], {
          amount: money(extra.amount),
          month: extra.appliedTo ? month(extra.appliedTo) : "",
        })
      : "",
  ].filter(Boolean);
  const actions = [
    payment.receiptPath
      ? `<button type="button" class="btn btn-ghost btn-sm" data-receipt="${esc(
          payment.receiptPath,
        )}">${esc(t("units.receipt"))}</button>`
      : "",
    payment.status === "rejected"
      ? `<button type="button" class="btn btn-ghost btn-sm" data-send-again>${esc(
          t("history.sendAgain"),
        )}</button>`
      : "",
  ].join("");
  return card(
    `<div class="history-head"><strong class="history-amount">${esc(
      money(payment.amount),
    )}</strong>${payment.status === "rejected" ? `<span class="pill pill-unpaid">${esc(paymentState(payment.status))}</span>` : pill(PAYMENT_PILL[payment.status])}</div>` +
      `<p class="hint">${esc(meta)}</p>` +
      lines.map((line) => `<p class="history-line">${esc(line)}</p>`).join("") +
      (payment.reason
        ? `<div class="history-reason"><strong>${esc(t("history.whyTurnedDown"))}</strong><p>${esc(
            payment.reason,
          )}</p></div>`
        : "") +
      (actions === "" ? "" : `<div class="actions">${actions}</div>`),
    "history-item",
  );
}

function residentPay(session: Session, owed: number): View {
  const unitId = session.unitId ?? "";
  const owing = store.months
    .filter((m) => m.unitId === unitId && m.summary.balance > 0)
    .map((m) => m.summary)
    .sort((a, b) => comparePeriods(a.period, b.period));
  const oldest = owing[0];
  // Without Chapa the receipt has no stub, and the transfer is the only way.
  const stubless = store.chapa ? "" : " slip-whole";
  const bankOnly = store.chapa ? "" : " only";
  const picks = [
    ...(oldest && owing.length > 1
      ? [
          {
            amount: oldest.balance,
            label: t("pay.oldest", { month: month(oldest.period), amount: money(oldest.balance) }),
          },
        ]
      : []),
    ...(owed > 0 ? [{ amount: owed, label: t("pay.all", { amount: money(owed) }) }] : []),
  ];
  /*
   * Paying ahead, priced by the engine the verification runs, and sent with
   * the number of months so verification credits them rather than holding the
   * money. Without the count, everything past what was owed went to the
   * accountant's held funds instead of the months the resident meant.
   */
  const ahead =
    store.monthlyDue > 0
      ? AHEAD_CHOICES.map((count) => {
          const plan = planPayment({
            entries: store.entries,
            monthlyDue: store.monthlyDue,
            currentPeriod: store.period,
            monthsAhead: count,
          });
          return { count, amount: plan.total, months: plan.lines.map((line) => line.period) };
        })
      : [];
  const allPicks = [
    ...picks.map((pick) => ({ ...pick, count: 0 })),
    ...ahead.map((choice) => ({
      amount: choice.amount,
      count: choice.count,
      label:
        choice.count === 1
          ? t("pay.aheadOne", { amount: money(choice.amount) })
          : t("pay.aheadMany", { count: choice.count, amount: money(choice.amount) }),
    })),
  ];
  /** The months ahead an amount stands for: those of the choice it matches, or none. */
  const aheadOf = (amount: number): number =>
    ahead.find((choice) => choice.amount === amount)?.count ?? 0;
  const monthsOf = (amount: number): Period[] =>
    ahead.find((choice) => choice.amount === amount)?.months ?? [];

  return {
    title: t("nav.pay"),
    sub: unitId,
    html:
      telebirrReturn() +
      chapaReturn() +
      `<section class="slip" aria-labelledby="payReceiptLabel"><div class="slip-main${stubless}">` +
      `<div class="slip-meta"><span id="payReceiptLabel">${esc(t("pay.receipt"))}${sayOther(
        "pay.receipt",
      )}</span><span>${esc(month(store.period))}</span></div>` +
      receiptLine(
        "pay.from",
        `<span class="receipt-value">${esc(t("pay.unit", { unit: unitId }))}</span>`,
      ) +
      receiptLine(
        "pay.amount",
        `<span class="receipt-value receipt-amount"><input id="payAmount" type="number" min="1" step="1" inputmode="numeric" required value="${Math.max(
          0,
          owed,
        )}" aria-describedby="payCovers" /><span class="unit">${esc(t("home.currency"))}</span></span>`,
        "payAmount",
      ) +
      (allPicks.length > 0
        ? `<div class="quick-picks" role="group" aria-label="${esc(t("pay.amount"))}">${allPicks
            .map(
              (pick) =>
                `<button type="button" class="quick-pick" data-amount="${pick.amount}" aria-pressed="${String(
                  pick.amount === owed,
                )}">${esc(pick.label)}</button>`,
            )
            .join("")}</div>`
        : "") +
      `<p class="hint pay-any">${esc(t("pay.anyAmount"))}</p>` +
      receiptLine(
        "pay.for",
        `<span class="receipt-value" id="payCovers" aria-live="polite">${esc(coverage(owed, owing))}</span>`,
      ) +
      receiptLine("pay.to", `<span class="receipt-value">${esc(store.orgName)}</span>`) +
      `</div>` +
      // Chapa's checkout takes telebirr itself, so where Chapa is on, its
      // stub is the one way to pay online and the Telebirr button is left out.
      (store.chapa
        ? `<div class="slip-stub"><button type="button" class="slip-pay" id="payChapa"><span class="main-line" id="payChapaLabel">${esc(
            t("chapa.pay"),
          )}</span><span class="other-line" lang="${otherLanguage()}" id="payChapaOther">${esc(
            other("pay.withChapa", { amount: money(owed) }),
          )}</span></button><span class="slip-methods">${esc(t("chapa.hint"))}</span></div>`
        : "") +
      `</section>` +
      `<section class="bank-slip${bankOnly}" aria-labelledby="bankTitle">` +
      `<h2 id="bankTitle">${esc(t("pay.bankTitle"))}${sayOther("pay.bankTitle")}</h2>` +
      `<p class="hint">${esc(t("pay.bankLead"))}</p>` +
      `<div class="form-grid form-grid-two" style="margin-top:1rem">${field(
        "payRef",
        t("resident.reference"),
        `placeholder="${esc(t("resident.referenceHint"))}"`,
      )}${receiptField("payReceipt")}</div>` +
      `<div class="actions"><button type="button" class="btn btn-ghost" id="submitPay">${esc(
        t("resident.submit"),
      )}</button></div>` +
      `<p class="hint">${esc(t("resident.partialPaymentHint"))}</p>` +
      (store.chapa
        ? ""
        : `<div class="telebirr-pay"><div class="actions"><button type="button" class="btn btn-ghost" id="payTelebirr" disabled aria-describedby="telebirrLater">${esc(
            t("telebirr.pay"),
          )}</button></div><p class="hint" id="telebirrLater">${esc(t("telebirr.notYet"))}</p></div>`) +
      `</section>`,
    wire: (root, go) => {
      const amountInput = root.querySelector<HTMLInputElement>("#payAmount");
      // The amount drives the rest of the slip: the months it reaches, the
      // stub's figure, and which quick choice, if any, it is.
      const follow = (): void => {
        const amount = Number(amountInput?.value);
        const covers = root.querySelector("#payCovers");
        if (covers) covers.textContent = coverage(amount, owing, monthsOf(amount));
        const valid = Number.isSafeInteger(amount) && amount > 0;
        const otherLine = root.querySelector("#payChapaOther");
        if (otherLine) {
          otherLine.textContent = valid
            ? other("pay.withChapa", { amount: money(amount) })
            : other("chapa.pay");
        }
        const mainLine = root.querySelector("#payChapaLabel");
        if (mainLine) {
          mainLine.textContent = valid
            ? t("pay.withChapa", { amount: money(amount) })
            : t("chapa.pay");
        }
        root.querySelectorAll<HTMLButtonElement>(".quick-pick").forEach((button) => {
          button.setAttribute("aria-pressed", String(Number(button.dataset.amount) === amount));
        });
      };
      amountInput?.addEventListener("input", follow);
      root.querySelectorAll<HTMLButtonElement>(".quick-pick").forEach((button) => {
        button.addEventListener("click", () => {
          if (amountInput) amountInput.value = button.dataset.amount ?? "";
          follow();
        });
      });
      follow();
      root.querySelector("#telebirrCheck")?.addEventListener("click", (event) => {
        const orderId = store.telebirrOrder?.id;
        if (!orderId) return;
        void act(pressed(event), async () => {
          await data.checkTelebirrPayment(session.orgId, { orderId });
        });
      });
      root.querySelector("#chapaCheck")?.addEventListener("click", (event) => {
        const orderId = store.chapaOrder?.id;
        if (!orderId) return;
        void act(pressed(event), async () => {
          await data.checkChapaPayment(session.orgId, { orderId });
        });
      });
      root.querySelector("#payChapa")?.addEventListener("click", (event) => {
        const input = root.querySelector<HTMLInputElement>("#payAmount");
        const amount = Number(input?.value);
        if (!Number.isSafeInteger(amount) || amount <= 0) {
          toast(t("resident.invalidPaymentAmount"));
          input?.focus();
          return;
        }
        void act(pressed(event), async () => {
          const { checkoutUrl } = await data.startChapaPayment(session.orgId, {
            amount,
            monthsAhead: aheadOf(amount),
          });
          window.location.assign(checkoutUrl);
        });
      });
      root.querySelector("#submitPay")?.addEventListener("click", (event) => {
        const input = root.querySelector<HTMLInputElement>("#payAmount");
        const amount = Number(input?.value);
        if (!Number.isSafeInteger(amount) || amount <= 0) {
          toast(t("resident.invalidPaymentAmount"));
          input?.focus();
          return;
        }
        const evidence = evidenceFrom(root, "payRef", "payReceipt");
        if (!evidence) {
          return;
        }
        void act(pressed(event), async () => {
          const receiptPath = await data.uploadReceipt(
            session.orgId,
            unitId,
            session.termId ?? "",
            evidence.file,
          );
          await data.submitPayment(session.orgId, {
            unitId,
            termId: session.termId ?? "",
            buildingId: session.buildingId ?? "",
            amount,
            monthsAhead: aheadOf(amount),
            customAmount: !allPicks.some((pick) => pick.amount === amount),
            reference: evidence.reference,
            campaignId: null,
            submittedBy: session.user.uid,
            receiptPath,
          });
          toast(t("resident.sent"));
          go("home");
        });
      });
    },
  };
}

/**
 * The order a resident came back from Telebirr with. Only the function moves
 * it to paid, after asking Telebirr, so until then this says it is waiting and
 * never that the money arrived.
 */
function telebirrReturn(): string {
  const followed = store.telebirrOrder;
  if (!followed) return "";
  const order = followed.order;
  const status = order?.status ?? "started";
  const message =
    status === "paid"
      ? t("telebirr.paid", { amount: money(order?.amount ?? 0) })
      : status === "needsReview"
        ? t("telebirr.needsReview")
        : status === "failed"
          ? t("telebirr.failed")
          : t("telebirr.waiting");
  return card(
    `<h3>${esc(t("telebirr.title"))}</h3><p>${esc(message)}</p>` +
      (status === "started"
        ? `<div class="actions">${gold("telebirrCheck", t("telebirr.check"))}</div>`
        : ""),
  );
}

/**
 * The order a resident came back from Chapa with. Only the function moves it
 * to paid, after asking Chapa, so until then this says it is waiting and never
 * that the money arrived.
 */
function chapaReturn(): string {
  const followed = store.chapaOrder;
  if (!followed) return "";
  const order = followed.order;
  const status = order?.status ?? "started";
  const message =
    status === "paid"
      ? t("chapa.paid", { amount: money(order?.amount ?? 0) })
      : status === "needsReview"
        ? t("chapa.needsReview")
        : status === "failed"
          ? t("chapa.failed")
          : t("chapa.waiting");
  return card(
    `<h3>${esc(t("chapa.title"))}</h3><p>${esc(message)}</p>` +
      (status === "started"
        ? `<div class="actions">${gold("chapaCheck", t("chapa.check"))}</div>`
        : ""),
  );
}

/** One line of the receipt: what it is, in both languages, and its value. */
function receiptLine(key: Key, value: string, labelFor = ""): string {
  const label = `${esc(t(key))}${sayOther(key)}`;
  return `<div class="receipt-line">${
    labelFor === ""
      ? `<span class="receipt-key">${label}</span>`
      : `<label class="receipt-key" for="${esc(labelFor)}">${label}</label>`
  }${value}</div>`;
}

/** A notification's words, in the language the page is in. */
function said(item: { notification: Parameters<typeof messageFor>[0] }) {
  return messageFor(item.notification, language() === "am" ? "am" : "en");
}

/** Everything Liyu has told this resident, newest first, unread marked. */
function inboxList(): View {
  return {
    title: t("inbox.title"),
    sub: "",
    html:
      store.inbox.length === 0
        ? empty(t("inbox.none"))
        : card(
            `<div class="row-list">${store.inbox
              .map((item) => {
                const { title, body } = said(item);
                const firstLine = body.split("\n")[0] ?? "";
                const preview = firstLine.length > 110 ? `${firstLine.slice(0, 107)}…` : firstLine;
                const unread = item.readAt === null;
                return (
                  `<button type="button" class="inbox-row${unread ? " unread" : ""}" data-note="${esc(item.id)}">` +
                  `<span class="inbox-dot" aria-hidden="true"></span>` +
                  `<span class="inbox-text"><strong>${esc(title)}</strong><span>${esc(preview)}</span></span>` +
                  `<span class="inbox-when">${esc(on(item.createdAt))}${
                    unread ? `<span class="inbox-new">${esc(t("inbox.new"))}</span>` : ""
                  }</span></button>`
                );
              })
              .join("")}</div>`,
          ),
    wire: (root, go) => {
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-note]")) {
        button.addEventListener("click", () => go(`notification:${button.dataset.note ?? ""}`));
      }
    },
  };
}

/** One notification in full. Opening it is what marks it read. */
function inboxItem(session: Session, id: string): View {
  const item = store.inbox.find((row) => row.id === id);
  if (!item) {
    return {
      title: t("inbox.title"),
      sub: "",
      html: empty(t("inbox.missing")),
      wire: (root, go) => wireBack(root, go),
    };
  }
  const { title, body } = said(item);
  return {
    title,
    sub: on(item.createdAt),
    html:
      `<div class="actions" style="margin-top:0"><button type="button" class="btn btn-ghost btn-sm" id="inboxBack">${esc(
        t("inbox.back"),
      )}</button></div>` +
      card(`<p class="inbox-body">${esc(body)}</p>`, "inbox-message") +
      (item.notification.kind === "rentDue"
        ? `<div class="actions"><button type="button" class="btn btn-gold" id="inboxPay">${esc(
            t("nav.pay"),
          )}</button></div>`
        : ""),
    wire: (root, go) => {
      wireBack(root, go);
      root.querySelector("#inboxPay")?.addEventListener("click", () => go("pay"));
      if (item.readAt === null) {
        void data.markRead(session.orgId, item.id).catch((cause: Error) => {
          console.error("Marking a notification read failed", cause);
        });
      }
    },
  };
}

function wireBack(root: HTMLElement, go: (view: string) => void): void {
  root.querySelector("#inboxBack")?.addEventListener("click", () => go("notifications"));
}

function residentRepairs(session: Session): View {
  const unitId = session.unitId ?? "";
  const mine = store.complaints.filter((c) => c.unitId === unitId || c.unitId === null);
  return {
    title: t("nav.repairs"),
    sub: "",
    html:
      card(
        `<h3>${esc(t("resident.newRepair"))}</h3><div class="form-grid">` +
          field(
            "repairTitle",
            t("resident.whatBroke"),
            `placeholder="${esc(t("resident.whatBrokeHint"))}"`,
          ) +
          `<div><label for="repairCat">${esc(t("books.category"))}</label><select class="field" id="repairCat">${COMPLAINT_CATEGORIES.map(
            (c) => `<option value="${esc(c)}">${esc(named("category", c))}</option>`,
          ).join("")}</select></div>` +
          `</div><div class="actions">${gold("newRepair", t("resident.raise"))}</div>`,
      ) +
      sectionTitle(t("resident.yourRequests")) +
      (mine.length === 0
        ? empty()
        : card(
            rowList(
              mine.map((c) =>
                row(
                  c.description,
                  [c.unitId, on(c.at)].filter((part) => part !== null).join(" · "),
                  pill(c.status) +
                    (movesFrom(c.status, "resident").includes("confirmed")
                      ? ` <button type="button" class="btn btn-ghost btn-sm" data-confirm-repair="${esc(c.id)}">${esc(t("resident.confirmFixed"))}</button>`
                      : "") +
                    (movesFrom(c.status, "resident").includes("reported")
                      ? ` <button type="button" class="btn btn-ghost btn-sm" data-not-fixed="${esc(c.id)}">${esc(t("resident.notFixed"))}</button>`
                      : ""),
                ),
              ),
            ),
          )),
    wire: (root, go) => {
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-confirm-repair]")) {
        button.addEventListener("click", (event) => {
          const complaint = mine.find((c) => c.id === button.dataset.confirmRepair);
          if (!complaint) return;
          void act(pressed(event), async () => {
            await data.moveComplaint(
              session.orgId,
              complaint,
              "confirmed",
              "resident",
              session.user.uid,
            );
            toast(t("resident.confirmedFixed"));
          });
        });
      }
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-not-fixed]")) {
        button.addEventListener("click", (event) => {
          const complaint = mine.find((c) => c.id === button.dataset.notFixed);
          if (!complaint) return;
          void act(pressed(event), async () => {
            await data.moveComplaint(
              session.orgId,
              complaint,
              "reported",
              "resident",
              session.user.uid,
            );
            toast(t("resident.sentBack"));
          });
        });
      }
      root.querySelector("#newRepair")?.addEventListener("click", (event) => {
        const description =
          root.querySelector<HTMLInputElement>("#repairTitle")?.value.trim() ?? "";
        if (description === "") {
          toast(t("resident.needTitle"));
          return;
        }
        void act(pressed(event), async () => {
          await data.raiseComplaint(session.orgId, {
            unitId,
            buildingId: session.buildingId ?? "",
            termId: session.termId ?? "",
            description,
            category: category(root.querySelector<HTMLSelectElement>("#repairCat")?.value),
            reporterUid: session.user.uid,
          });
          toast(t("resident.raised"));
          go("repairs");
        });
      });
    },
  };
}

function residentGive(session: Session): View {
  const open = store.campaigns.find((c) => c.status === "open");
  if (!open) {
    return { title: t("nav.give"), sub: "", html: empty() };
  }
  return {
    title: t("nav.give"),
    sub: "",
    html: card(
      `<h3>${esc(t("resident.giveOpen"))}</h3>` +
        `<div class="stat-value" style="font-size:1.35rem">${esc(open.title)}</div>` +
        `<div class="split" style="margin-top:1rem"><span>${esc(
          t("resident.raisedOf", { raised: money(open.raised), target: money(open.target) }),
        )}</span></div>` +
        bar(open.target === 0 ? 0 : open.raised / open.target) +
        `<div class="form-grid">${field(
          "giveAmt",
          t("resident.giveAmount"),
          'type="number" min="1" step="1"',
        )}${field("giveRef", t("resident.reference"))}${receiptField("giveReceipt")}</div>` +
        `<div class="actions">${gold("giveBtn", t("resident.giveSend"))}</div>` +
        `<p class="hint">${esc(t("resident.giveHint"))}</p>`,
    ),
    wire: (root, go) => {
      root.querySelector("#giveBtn")?.addEventListener("click", (event) => {
        const raw = root.querySelector<HTMLInputElement>("#giveAmt")?.value ?? "";
        const amount = Number(raw);
        if (!Number.isInteger(amount) || amount < 1) {
          toast(t("resident.giveAmountBad"));
          return;
        }
        const evidence = evidenceFrom(root, "giveRef", "giveReceipt");
        if (!evidence) {
          return;
        }
        void act(pressed(event), async () => {
          const receiptPath = await data.uploadReceipt(
            session.orgId,
            session.unitId ?? "",
            session.termId ?? "",
            evidence.file,
          );
          await data.submitPayment(session.orgId, {
            unitId: session.unitId ?? "",
            termId: session.termId ?? "",
            buildingId: session.buildingId ?? "",
            amount,
            monthsAhead: 0,
            reference: evidence.reference,
            receiptPath,
            campaignId: open.id,
            submittedBy: session.user.uid,
          });
          toast(t("resident.gaveIt"));
          go("give");
        });
      });
    },
  };
}

/* ---------------------------------------------------------- accountant -- */

export function accountant(view: string, session: Session): View {
  if (view === "advertisements") {
    return {
      title: t("nav.advertisements"),
      sub: "",
      html: advertisementList(),
      wire: (root) => wireContractorDirectory(root),
    };
  }

  if (view === "units") {
    return unitsTable(session);
  }
  if (view === "books") {
    return books(session);
  }
  if (view === "arrears") {
    return arrearsTable(session);
  }
  if (view === "journal") {
    return journal();
  }
  if (view === "chapa") {
    return chapaView("accountant", session);
  }
  return queue(session);
}

/** The review button says whether its panel is open. The unit link keeps its text. */
function labelReview(root: HTMLElement, paymentId: string): void {
  const panel = root.querySelector<HTMLElement>(`[data-review-panel="${CSS.escape(paymentId)}"]`);
  const open = panel !== null && !panel.classList.contains("hidden");
  for (const button of root.querySelectorAll<HTMLButtonElement>(
    `[data-review-toggle="${CSS.escape(paymentId)}"][data-review-label]`,
  )) {
    button.textContent = t(open ? "queue.hideReview" : "queue.review");
    button.setAttribute("aria-expanded", String(open));
  }
}

function queueReview(payment: data.Payment): string {
  const unit = store.units.find((item) => item.unitId === payment.unitId);
  const campaign = payment.campaignId
    ? (store.campaigns.find((item) => item.id === payment.campaignId)?.title ?? payment.campaignId)
    : "—";
  return `<div class="queue-review"><div class="queue-review-heading"><div><p class="eyebrow">${esc(t("queue.review"))}</p><h3>${esc(payment.unitId)}</h3></div><span class="pill pill-unverified">${esc(t("queue.waiting"))}</span></div><div class="queue-review-grid"><div><span>${esc(t("queue.block"))}</span><strong>${esc(unit?.block ?? "—")}</strong></div><div><span>${esc(t("queue.amount"))}</span><strong>${esc(money(payment.amount))}</strong></div><div><span>${esc(t("queue.months"))}</span><strong>${esc(payment.customAmount ? t("queue.customAmount") : String(payment.monthsAhead + 1))}</strong></div><div><span>${esc(t("queue.when"))}</span><strong>${esc(on(payment.submittedAt))}</strong></div><div><span>${esc(t("queue.reference"))}</span><strong>${esc(payment.reference ?? "—")}</strong></div><div><span>${esc(t("queue.campaign"))}</span><strong>${esc(campaign)}</strong></div><div><span>${esc(t("queue.paymentId"))}</span><strong class="mono">${esc(payment.id)}</strong></div></div><div class="queue-review-actions">${payment.receiptPath ? `<button type="button" class="btn btn-ghost btn-sm" data-review-receipt="${esc(payment.receiptPath)}">${esc(t("units.receipt"))}</button>` : `<span class="hint">${esc(t("queue.noReceipt"))}</span>`}</div></div>`;
}

function queue(session: Session): View {
  const worth = store.waiting.reduce((sum, p) => sum + p.amount, 0);
  const heldTotal = store.held.reduce((sum, h) => sum + h.amount, 0);
  const sums = totals();

  const head =
    grid(3, [
      stat(
        t("queue.waiting"),
        String(store.waiting.length),
        t("queue.waitingSub", { amount: money(worth) }),
      ),
      stat(t("queue.held"), money(heldTotal), t("queue.heldSub")),
      stat(t("queue.collected"), money(sums.month.collected), t("queue.collectedSub")),
    ]) + sectionTitle(t("queue.heading"));

  if (store.waiting.length === 0) {
    return {
      title: t("nav.queue"),
      sub: store.orgName,
      html:
        head +
        card(
          `<div class="empty"><div class="stat-value">✓</div><strong>${esc(
            t("queue.clear"),
          )}</strong><p class="hint">${esc(t("queue.clearSub"))}</p></div>`,
        ),
    };
  }

  return {
    title: t("nav.queue"),
    sub: t("queue.waitingSub", { amount: money(worth) }),
    html:
      head +
      table(
        [
          t("queue.unit"),
          t("queue.months"),
          t("queue.amount"),
          t("queue.reference"),
          t("queue.when"),
          "",
        ],
        store.waiting.map(
          (p) =>
            `<tr><td><button type="button" class="unit-link" data-review-toggle="${esc(p.id)}">${esc(p.unitId)}</button></td>` +
            `<td>${esc(p.customAmount ? t("queue.customAmount") : String(p.monthsAhead + 1))}</td>` +
            `<td>${esc(money(p.amount))}</td>` +
            `<td>${esc(p.reference ?? "—")}</td>` +
            `<td>${esc(on(p.submittedAt))}</td>` +
            `<td style="white-space:nowrap">` +
            `<button type="button" class="btn btn-ghost btn-sm" data-review-toggle="${esc(p.id)}" data-review-label>${esc(
              t("queue.review"),
            )}</button> ` +
            `<button type="button" class="btn btn-gold btn-sm" data-verify="${esc(p.id)}">${esc(
              t("queue.verify"),
            )}</button> ` +
            `<button type="button" class="btn btn-danger btn-sm" data-reject="${esc(p.id)}">${esc(
              t("queue.reject"),
            )}</button></td></tr>` +
            `<tr class="queue-review-row hidden" data-review-panel="${esc(p.id)}" data-panel="review-${esc(p.id)}"><td colspan="6">${queueReview(p)}</td></tr>`,
        ),
      ),
    wire: (root, go) => {
      /*
       * Both buttons in a row are held together, and the row is dimmed.
       *
       * Verifying and rejecting the same payment are the same decision made
       * two ways, so leaving the other button live while one is in flight
       * invites an accountant to press both. The row is dimmed rather than
       * removed: it leaves when the payment really changes and the listener
       * says so. A row that disappeared on the press and came back on a
       * refusal would be the console telling the accountant that a payment
       * was dealt with when it was not.
       */
      const working = (button: HTMLButtonElement): (() => void) => {
        const line = button.closest("tr");
        const pair = [...(line?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
        line?.classList.add("row-working");
        for (const other of pair) {
          other.disabled = true;
        }
        return () => {
          line?.classList.remove("row-working");
          for (const other of pair) {
            other.disabled = false;
          }
        };
      };

      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-review-toggle]")) {
        button.addEventListener("click", () => {
          const paymentId = button.dataset.reviewToggle ?? "";
          const panel = root.querySelector<HTMLElement>(
            `[data-review-panel="${CSS.escape(paymentId)}"]`,
          );
          if (!panel) return;
          panel.classList.toggle("hidden");
          labelReview(root, paymentId);
        });
      }
      // A panel a live update kept open says so on its button.
      for (const panel of root.querySelectorAll<HTMLElement>("[data-review-panel]")) {
        labelReview(root, panel.dataset.reviewPanel ?? "");
      }
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-review-receipt]")) {
        button.addEventListener("click", () => {
          const original = button.textContent ?? "";
          button.disabled = true;
          button.textContent = t("units.receiptLoading");
          void data
            .receiptUrl(button.dataset.reviewReceipt ?? "")
            .then((url) => showReceipt(url))
            .catch(() => {
              toast(t("units.receiptUnavailable"));
            })
            .finally(() => {
              button.disabled = false;
              button.textContent = original;
            });
        });
      }

      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-verify]")) {
        button.addEventListener("click", () => {
          const paymentId = button.dataset.verify ?? "";
          const done = working(button);
          void act(button, async () => {
            await data.verifyPayment(session.orgId, { paymentId });
            toast(t("queue.verified"));
          }).finally(done);
        });
      }
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-reject]")) {
        button.addEventListener("click", () => {
          const paymentId = button.dataset.reject ?? "";
          const reason = window.prompt(t("queue.why"));
          if (reason === null) {
            return;
          }
          if (reason.trim() === "") {
            toast(t("queue.needWhy"));
            return;
          }
          const done = working(button);
          void act(button, async () => {
            await data.rejectPayment(session.orgId, { paymentId, reason: reason.trim() });
            toast(t("queue.rejected"));
          }).finally(done);
        });
      }
      void go;
    },
  };
}

/**
 * What the units list is narrowed to. Each filter stands on its own, so "block
 * A, not paid" is two choices rather than one button that has to exist for it.
 * Kept for the session, so coming back to the list finds it as it was left.
 */
const unitFilter = {
  block: "all",
  status: "all",
  search: "",
  sort: "unit" as "unit" | "owed",
};
let selectedUnit = "";

/** The payment states the list can be narrowed to, in the order they are offered. */
const UNIT_STATUSES: [string, Key][] = [
  ["all", "units.all"],
  ["unpaid", "state.unpaid"],
  ["partial", "state.partial"],
  ["unverified", "state.unverified"],
  ["verified", "units.current"],
  ["inactive", "state.inactive"],
];

function matchesStatus(state: string, status: string): boolean {
  if (status === "all") return true;
  return state === status;
}

function matchesSearch(unitId: string, search: string): boolean {
  const wanted = search.trim().toUpperCase();
  return wanted === "" || unitId.toUpperCase().includes(wanted);
}

function unitDetails(unitId: string, session: Session): string {
  const tile = map().tiles.find((item) => item.unitId === unitId);
  followUnit(session.orgId, unitId);
  const payments =
    store.unitPayments?.unitId === unitId
      ? [...store.unitPayments.rows].sort(
          (a, b) => (b.submittedAt?.getTime() ?? 0) - (a.submittedAt?.getTime() ?? 0),
        )
      : [];
  const paymentRows = payments
    .map((payment) => {
      const status =
        payment.status === "submitted"
          ? "unverified"
          : payment.status === "verified"
            ? "verified"
            : "unpaid";
      const actions = `<div class="unit-payment-actions">${
        payment.receiptPath
          ? `<button type="button" class="btn btn-ghost btn-sm" data-receipt="${esc(payment.receiptPath)}">${esc(t("units.receipt"))}</button>`
          : ""
      }${
        session.role === "accountant" && payment.status === "submitted"
          ? `<button type="button" class="btn btn-gold btn-sm" data-unit-verify="${esc(payment.id)}">${esc(t("queue.verify"))}</button><button type="button" class="btn btn-danger btn-sm" data-unit-reject="${esc(payment.id)}">${esc(t("queue.reject"))}</button>`
          : ""
      }${
        session.role === "accountant" && payment.status === "verified"
          ? `<button type="button" class="btn btn-ghost btn-sm" data-unit-reverse="${esc(payment.id)}">${esc(t("fix.takeBack"))}</button>`
          : ""
      }</div>`;
      return `<div class="row unit-payment-row"><div class="left"><strong>${esc(money(payment.amount))}</strong><span>${esc(t("units.paymentReference"))}: ${esc(payment.reference ?? "—")}</span><span>${esc(t("units.paymentDate"))}: ${esc(on(payment.submittedAt))}</span></div><div class="right"><div>${pill(status)}</div>${actions}</div></div>`;
    })
    .join("");
  return card(
    `<div class="unit-detail-head"><div><p class="eyebrow">${esc(t("units.details"))}</p><h3>${esc(unitId)}</h3><div class="unit-detail-meta"><span>${esc(tile?.block ?? "")}</span><span>${pill(tile?.state ?? "unpaid")}</span><span>${esc(money(tile?.balance ?? 0))}</span></div></div><button type="button" class="btn btn-ghost btn-sm" data-close-unit>${esc(t("units.closeDetails"))}</button></div>` +
      `<div class="unit-payments"><h4>${esc(t("units.paymentHistory"))}</h4>${paymentRows === "" ? `<p class="hint">${esc(t("units.noPayments"))}</p>` : `<div class="row-list">${paymentRows}</div>`}</div>` +
      (offers(
        session.role === "committee" ? "committeeResetResident" : "resetResident",
        "desktop",
        session.role,
      )
        ? signInReset(unitId)
        : "") +
      (offers("handOver", "desktop", session.role) ? occupancy(unitId) : "") +
      (session.role === "accountant" ? remindOne(unitId) : "") +
      (session.role === "accountant" ? corrections(unitId) : ""),
    "unit-detail",
  );
}

/** The accountant reminding this one unit, when the books say it owes. */
function remindOne(unitId: string): string {
  const row = behind().rows.find((r) => r.unitId === unitId);
  if (!row || !offers("sendReminders", "desktop", "accountant", store.entitlements)) return "";
  return (
    `<div class="unit-corrections"><h4>${esc(t("arrears.remindTitle"))}</h4>` +
    `<p class="hint">${esc(t("arrears.oweLine", { amount: money(row.owed), count: row.months }))}</p>` +
    `<div class="form-grid"><div><label for="unitRemindNote">${esc(t("arrears.note"))}</label>` +
    `<textarea class="field" id="unitRemindNote" rows="2" maxlength="${REMINDER_NOTE_LIMIT}" placeholder="${esc(
      t("arrears.noteHint"),
    )}"></textarea></div></div>` +
    `<div class="actions"><button type="button" class="btn btn-gold" id="unitRemind">${esc(
      t("arrears.remind"),
    )}</button></div></div>`
  );
}

/**
 * The code a reset issued, kept until the unit is closed so a live redraw does
 * not lose it before it is written down. It is never stored anywhere else.
 */
let issuedCode: { unitId: string; password: string } | null = null;

function signInReset(unitId: string): string {
  const issued =
    issuedCode?.unitId === unitId
      ? `<p class="hint">${esc(t("signInReset.issued", { unit: unitId }))}</p><p class="issued-code"><code>${esc(issuedCode.password)}</code></p>`
      : "";
  return (
    `<div class="unit-corrections"><h4>${esc(t("signInReset.title"))}</h4><p class="hint">${esc(t("signInReset.hint"))}</p>` +
    `<div class="form-grid form-grid-two">${field("resetWhy", t("fix.why"), `placeholder="${esc(t("fix.whyHint"))}"`)}</div>` +
    `<div class="actions">${gold("resetSignIn", t("signInReset.button"))}</div>${issued}</div>`
  );
}

/** The unit's resident account, and a new occupant moving in. The accountant's. */
function occupancy(unitId: string): string {
  const account = store.logins.find(
    (login) => login.appRole === "resident" && login.unitId === unitId,
  );
  const handed = handedOver?.unitId === unitId ? handedOver : null;
  const state = !account
    ? t("occupancy.noAccount")
    : [
        account.name,
        account.phone,
        t(account.setUp ? "occupancy.setUp" : "occupancy.waiting"),
        account.active ? null : t("occupancy.switchedOff"),
      ]
        .filter((part) => part)
        .join(" · ");
  return (
    `<div class="unit-corrections"><h4>${esc(t("occupancy.title"))}</h4><p>${esc(state)}</p>` +
    (account
      ? `<div class="actions"><button type="button" class="btn btn-ghost btn-sm" data-account-toggle="${esc(
          account.uid,
        )}" data-active="${account.active ? "1" : "0"}">${esc(
          t(account.active ? "occupancy.switchOff" : "occupancy.switchOn"),
        )}</button></div>`
      : "") +
    `<h5>${esc(t("occupancy.handOver"))}</h5><p class="hint">${esc(t("occupancy.handOverHint"))}</p>` +
    `<div class="form-grid form-grid-two">${field("handOverWhy", t("fix.why"), `placeholder="${esc(t("occupancy.handOverWhyHint"))}"`)}</div>` +
    `<div class="actions">${gold("handOver", t("occupancy.handOverButton"))}</div>` +
    (handed
      ? `<p class="hint">${esc(t("occupancy.handedOver", { unit: unitId }))}</p><p class="issued-code"><code>${esc(
          handed.password,
        )}</code></p>` +
        (handed.carriedBalance > 0
          ? `<p class="hint">${esc(t("occupancy.carried", { amount: money(handed.carriedBalance) }))}</p>`
          : "")
      : "") +
    `</div>`
  );
}

/** The code a handover issued, kept like a reset's until the unit is closed. */
let handedOver: { unitId: string; password: string; carriedBalance: number } | null = null;

/** The months a correction can name for this unit: those on its books, newest first. */
function periodsOf(unitId: string): Period[] {
  const known = new Set<Period>([store.period]);
  for (const row of store.months) {
    if (row.unitId === unitId) known.add(row.summary.period);
  }
  return [...known].sort().reverse();
}

function periodSelect(id: string, unitId: string): string {
  return `<div><label for="${esc(id)}">${esc(t("fix.whichMonth"))}</label><select class="field" id="${esc(id)}">${periodsOf(
    unitId,
  )
    .map((period) => `<option value="${esc(period)}">${esc(month(period))}</option>`)
    .join("")}</select></div>`;
}

/**
 * The accountant's corrections for one unit. Every one needs a reason, which
 * the function writes to the journal with the change.
 */
function corrections(unitId: string): string {
  const unit = store.units.find((u) => u.unitId === unitId);
  const reason = (id: string) => field(id, t("fix.why"), `placeholder="${esc(t("fix.whyHint"))}"`);
  return (
    `<div class="unit-corrections"><h4>${esc(t("fix.title"))}</h4>` +
    `<h5>${esc(t("fix.record"))}</h5><div class="form-grid form-grid-two">` +
    field("fixCashAmount", t("fix.amount"), 'type="number" min="1" step="1"') +
    reason("fixCashWhy") +
    `</div><div class="actions">${gold("fixCash", t("fix.record"))}</div>` +
    `<h5>${esc(t("fix.adjust"))}</h5><div class="form-grid form-grid-two">` +
    periodSelect("fixAdjustMonth", unitId) +
    `<div><label for="fixAdjustWay">${esc(t("fix.amount"))}</label><select class="field" id="fixAdjustWay"><option value="reduce">${esc(
      t("fix.reduce"),
    )}</option><option value="add">${esc(t("fix.add"))}</option></select></div>` +
    field("fixAdjustAmount", t("fix.amount"), 'type="number" min="1" step="1"') +
    reason("fixAdjustWhy") +
    `</div><div class="actions">${gold("fixAdjust", t("fix.post"))}</div>` +
    `<h5>${esc(t("fix.voidMonth"))}</h5><p class="hint">${esc(t("fix.voidHint"))}</p><div class="form-grid form-grid-two">` +
    periodSelect("fixVoidMonth", unitId) +
    reason("fixVoidWhy") +
    `</div><div class="actions">${gold("fixVoid", t("fix.cancelBill"))}</div>` +
    `<h5>${esc(t("fix.changeBilling"))}</h5><p class="hint">${esc(t("fix.nextRunOnly"))}</p><div class="form-grid form-grid-two">` +
    field(
      "fixDue",
      t("fix.monthlyDue"),
      `type="number" min="1" step="1" value="${esc(String(unit?.monthlyDue ?? ""))}"`,
    ) +
    `<div><label><input type="checkbox" id="fixPaused"${unit?.billingPaused ? " checked" : ""} /> ${esc(
      t("fix.paused"),
    )}</label></div>` +
    reason("fixDueWhy") +
    `</div><div class="actions">${gold("fixSaveBilling", t("fix.save"))}</div></div>`
  );
}

function unitsTable(session: Session): View {
  // Only the open unit's payments are followed; none when no unit is open.
  if (selectedUnit === "") {
    followUnit(session.orgId, null);
  }
  const blocks = [...new Set(store.units.map((u) => u.block))].sort(compareUnitIds);
  if (unitFilter.block !== "all" && !blocks.includes(unitFilter.block)) {
    unitFilter.block = "all";
  }
  const everyone = map().tiles;
  const chosenBlock = unitFilter.block;
  const chosenStatus = unitFilter.status;
  const drawn = map(unitFilter.block === "all" ? undefined : unitFilter.block);
  const tiles = drawn.tiles
    .filter((tile) => matchesStatus(tile.state, unitFilter.status))
    .sort((a, b) =>
      unitFilter.sort === "owed"
        ? b.balance - a.balance || compareUnitIds(a.unitId, b.unitId)
        : compareUnitIds(a.unitId, b.unitId),
    );
  const shown = tiles.filter((tile) => matchesSearch(tile.unitId, unitFilter.search));
  const narrowed =
    unitFilter.block !== "all" || unitFilter.status !== "all" || unitFilter.search.trim() !== "";
  const showing = (count: number, owed: number): string =>
    t("units.showing", { shown: count, total: everyone.length, owed: money(owed) });
  const owedBy = (list: { balance: number }[]): number =>
    list.reduce((sum, tile) => sum + tile.balance, 0);

  return {
    title: t("units.heading"),
    sub: t("units.sub"),
    html:
      `<div class="unit-filters">` +
      `<div><label for="unitBlock">${esc(t("units.blockColumn"))}</label><select class="field" id="unitBlock">` +
      [
        ["all", t("units.allBlocks")] as const,
        ...blocks.map((block) => [block, t("units.block", { block })] as const),
      ]
        .map(
          ([key, label]) =>
            `<option value="${esc(key)}"${key === chosenBlock ? " selected" : ""}>${esc(label)}</option>`,
        )
        .join("") +
      `</select></div>` +
      `<div><label for="unitSearch">${esc(t("units.search"))}</label><input class="field" id="unitSearch" type="search" autocomplete="off" placeholder="${esc(
        t("units.searchHint"),
      )}" value="${esc(unitFilter.search)}" /></div>` +
      `<div><label for="unitSort">${esc(t("units.sort"))}</label><select class="field" id="unitSort"><option value="unit"${
        unitFilter.sort === "unit" ? " selected" : ""
      }>${esc(t("units.sortUnit"))}</option><option value="owed"${
        unitFilter.sort === "owed" ? " selected" : ""
      }>${esc(t("units.sortOwed"))}</option></select></div>` +
      `<fieldset class="unit-status"><legend>${esc(t("units.filterStatus"))}</legend>${UNIT_STATUSES.map(
        ([key, label]) =>
          `<button type="button" class="quick-pick" data-status="${esc(key)}" aria-pressed="${String(
            key === chosenStatus,
          )}">${esc(t(label))}</button>`,
      ).join("")}</fieldset>` +
      `</div>` +
      `<div class="unit-count"><span id="unitCount" aria-live="polite">${esc(
        showing(shown.length, owedBy(shown)),
      )}</span>${
        narrowed
          ? `<button type="button" class="band-link unit-clear" id="unitClear">${esc(t("units.clear"))}</button>`
          : ""
      }</div>` +
      (selectedUnit !== "" && tiles.some((tile) => tile.unitId === selectedUnit)
        ? unitDetails(selectedUnit, session)
        : "") +
      `<div style="margin-top:1rem">` +
      table(
        [t("queue.unit"), t("units.blockColumn"), t("units.status"), t("units.balance")],
        tiles.map((tile) => {
          // Rows the search does not match are drawn hidden, so typing can show them.
          const hidden = matchesSearch(tile.unitId, unitFilter.search) ? "" : " hidden";
          return (
            `<tr data-row="${esc(tile.unitId)}" data-balance="${tile.balance}"${hidden}><td><button type="button" class="unit-link" data-unit="${esc(tile.unitId)}">${esc(tile.unitId)}</button></td><td>${esc(tile.block)}</td>` +
            `<td>${pill(tile.state)}</td><td>${esc(money(tile.balance))}</td></tr>`
          );
        }),
      ) +
      `<p class="empty" id="unitNone"${shown.length === 0 ? "" : " hidden"}>${esc(t("units.noMatch"))}</p>` +
      `</div>`,
    wire: (root, go) => {
      root.querySelector<HTMLSelectElement>("#unitBlock")?.addEventListener("change", (event) => {
        unitFilter.block = (event.target as HTMLSelectElement).value;
        go("units");
      });
      root.querySelector<HTMLSelectElement>("#unitSort")?.addEventListener("change", (event) => {
        unitFilter.sort = (event.target as HTMLSelectElement).value === "owed" ? "owed" : "unit";
        go("units");
      });
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-status]")) {
        button.addEventListener("click", () => {
          unitFilter.status = button.dataset.status ?? "all";
          go("units");
        });
      }
      root.querySelector("#unitClear")?.addEventListener("click", () => {
        unitFilter.block = "all";
        unitFilter.status = "all";
        unitFilter.search = "";
        go("units");
      });
      // Typing narrows the rows already drawn rather than redrawing the page,
      // so the box keeps its focus and the cursor stays where it was.
      root.querySelector<HTMLInputElement>("#unitSearch")?.addEventListener("input", (event) => {
        unitFilter.search = (event.target as HTMLInputElement).value;
        let count = 0;
        let owed = 0;
        for (const row of root.querySelectorAll<HTMLTableRowElement>("tr[data-row]")) {
          const match = matchesSearch(row.dataset.row ?? "", unitFilter.search);
          row.hidden = !match;
          if (match) {
            count += 1;
            owed += Number(row.dataset.balance ?? 0);
          }
        }
        const counter = root.querySelector("#unitCount");
        if (counter) counter.textContent = showing(count, owed);
        root.querySelector<HTMLElement>("#unitNone")?.toggleAttribute("hidden", count > 0);
      });
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-unit]")) {
        button.addEventListener("click", () => {
          selectedUnit = button.dataset.unit ?? "";
          go("units");
        });
      }
      root.querySelector<HTMLButtonElement>("#unitRemind")?.addEventListener("click", (event) => {
        const note = root.querySelector<HTMLTextAreaElement>("#unitRemindNote")?.value.trim() ?? "";
        void act(pressed(event), async () => {
          await data.remindUnits(session.orgId, { unitIds: [selectedUnit], note });
          toast(t("arrears.reminded"));
        });
      });
      root.querySelector<HTMLButtonElement>("[data-close-unit]")?.addEventListener("click", () => {
        selectedUnit = "";
        issuedCode = null;
        handedOver = null;
        go("units");
      });
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-receipt]")) {
        button.addEventListener("click", () => {
          const original = button.textContent ?? "";
          button.disabled = true;
          button.textContent = t("units.receiptLoading");
          void data
            .receiptUrl(button.dataset.receipt ?? "")
            .then((url) => showReceipt(url))
            .catch(() => {
              toast(t("units.receiptUnavailable"));
            })
            .finally(() => {
              button.disabled = false;
              button.textContent = original;
            });
        });
      }
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-unit-verify]")) {
        button.addEventListener("click", () => {
          void act(button, async () => {
            await data.verifyPayment(session.orgId, { paymentId: button.dataset.unitVerify ?? "" });
            toast(t("queue.verified"));
          });
        });
      }
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-unit-reject]")) {
        button.addEventListener("click", () => {
          const reason = window.prompt(t("queue.why"));
          if (reason === null) return;
          if (reason.trim() === "") {
            toast(t("queue.needWhy"));
            return;
          }
          void act(button, async () => {
            await data.rejectPayment(session.orgId, {
              paymentId: button.dataset.unitReject ?? "",
              reason: reason.trim(),
            });
            toast(t("queue.rejected"));
          });
        });
      }
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-unit-reverse]")) {
        button.addEventListener("click", () => {
          const reason = window.prompt(`${t("fix.takeBack")}. ${t("fix.reverseHint")}`);
          if (reason === null) return;
          if (reason.trim() === "") {
            toast(t("queue.needWhy"));
            return;
          }
          void act(button, async () => {
            await data.reversePayment(session.orgId, {
              paymentId: button.dataset.unitReverse ?? "",
              reason: reason.trim(),
            });
            toast(t("fix.done"));
          });
        });
      }
      const value = (id: string) =>
        root.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`)?.value.trim() ?? "";
      /** A whole number of birr above zero, or a toast and nothing. */
      const birr = (id: string): number | null => {
        const amount = Number(value(id));
        if (!Number.isSafeInteger(amount) || amount <= 0) {
          toast(t("fix.needAmount"));
          return null;
        }
        return amount;
      };
      const why = (id: string): string | null => {
        const reason = value(id);
        if (reason === "") {
          toast(t("queue.needWhy"));
          return null;
        }
        return reason;
      };
      const unitId = selectedUnit;
      root.querySelector("#fixCash")?.addEventListener("click", (event) => {
        const amount = birr("fixCashAmount");
        const reason = amount === null ? null : why("fixCashWhy");
        if (amount === null || reason === null) return;
        void act(pressed(event), async () => {
          await data.recordPayment(session.orgId, { unitId, amount, reason });
          toast(t("fix.done"));
        });
      });
      root.querySelector("#fixAdjust")?.addEventListener("click", (event) => {
        const amount = birr("fixAdjustAmount");
        const reason = amount === null ? null : why("fixAdjustWhy");
        if (amount === null || reason === null) return;
        void act(pressed(event), async () => {
          await data.postAdjustment(session.orgId, {
            unitId,
            period: value("fixAdjustMonth") as Period,
            amount: value("fixAdjustWay") === "add" ? amount : -amount,
            reason,
          });
          toast(t("fix.done"));
        });
      });
      root.querySelector("#fixVoid")?.addEventListener("click", (event) => {
        const reason = why("fixVoidWhy");
        if (reason === null) return;
        void act(pressed(event), async () => {
          await data.voidInvoice(session.orgId, {
            unitId,
            period: value("fixVoidMonth") as Period,
            reason,
          });
          toast(t("fix.done"));
        });
      });
      root.querySelector("#handOver")?.addEventListener("click", (event) => {
        const reason = why("handOverWhy");
        if (reason === null) return;
        if (!window.confirm(t("occupancy.handOverConfirm", { unit: unitId }))) return;
        void act(pressed(event), async () => {
          const done = await data.handOverUnit(session.orgId, { unitId, reason });
          handedOver = {
            unitId,
            password: done.password,
            carriedBalance: done.carriedBalance,
          };
          go("units");
        });
      });
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-account-toggle]")) {
        button.addEventListener("click", () => {
          const active = button.dataset.active !== "1";
          const reason = window.prompt(
            t(active ? "occupancy.switchOnWhy" : "occupancy.switchOffWhy"),
          );
          if (reason === null) return;
          if (reason.trim() === "") {
            toast(t("queue.needWhy"));
            return;
          }
          void act(button, async () => {
            await data.setAccountActive(session.orgId, {
              uid: button.dataset.accountToggle ?? "",
              active,
              reason: reason.trim(),
            });
            toast(t("fix.done"));
          });
        });
      }
      root.querySelector("#resetSignIn")?.addEventListener("click", (event) => {
        const reason = why("resetWhy");
        if (reason === null) return;
        void act(pressed(event), async () => {
          const reset = await data.resetPassword(session.orgId, { loginId: unitId, reason });
          issuedCode = { unitId, password: reset.password };
          go("units");
        });
      });
      root.querySelector("#fixSaveBilling")?.addEventListener("click", (event) => {
        const monthlyDue = birr("fixDue");
        const reason = monthlyDue === null ? null : why("fixDueWhy");
        if (monthlyDue === null || reason === null) return;
        const billingPaused = root.querySelector<HTMLInputElement>("#fixPaused")?.checked ?? false;
        void act(pressed(event), async () => {
          await data.changeUnit(session.orgId, { unitId, monthlyDue, billingPaused, reason });
          toast(t("fix.done"));
        });
      });
    },
  };
}

function books(session: Session): View {
  const sums = totals();
  const spent = store.expenses.filter((e) => !e.voided);
  return {
    title: t("nav.books"),
    sub: store.orgName,
    html:
      grid(4, [
        stat(t("books.collected"), money(sums.month.collected), t("books.collectedSub")),
        stat(t("books.spent"), money(sums.spent), t("books.spentSub")),
        stat(t("books.net"), money(sums.net), t("books.netSub")),
        stat(t("books.outstanding"), money(behind().owed), t("books.outstandingSub")),
      ]) +
      sectionTitle(t("statement.title")) +
      card(
        `<p class="hint" style="margin-top:0">${esc(t("statement.hint"))}</p>` +
          `<div class="actions">${gold("exportMonth", t("statement.export"))}</div>`,
      ) +
      sectionTitle(t("books.record")) +
      card(
        `<div class="form-grid form-grid-two">` +
          field("expAmt", t("books.amount"), 'type="number" min="1" step="1"') +
          `<div><label for="expCat">${esc(t("books.category"))}</label><select class="field" id="expCat">${EXPENSE_CATEGORIES.map(
            (c) => `<option value="${esc(c)}">${esc(named("category", c))}</option>`,
          ).join("")}</select></div>` +
          field("expVendor", t("books.vendor"), `placeholder="${esc(t("books.vendorHint"))}"`) +
          `<div style="grid-column:1/-1"><label for="expDesc">${esc(
            t("books.description"),
          )}</label><input class="field" id="expDesc" placeholder="${esc(
            t("books.descriptionHint"),
          )}" /></div>` +
          `</div><div class="actions">${gold("addExp", t("books.add"))}</div>`,
      ) +
      sectionTitle(t("books.recent")) +
      (spent.length === 0
        ? empty()
        : table(
            [
              t("books.date"),
              t("books.category"),
              t("books.vendor"),
              t("books.description"),
              t("books.amount"),
              "",
            ],
            spent.map(
              (e) =>
                `<tr><td>${esc(on(e.at))}</td><td>${esc(
                  named("category", e.category),
                )}</td><td>${esc(e.vendor)}</td><td>${esc(e.description)}</td><td>${esc(
                  money(e.amount),
                )}</td><td><button type="button" class="btn btn-ghost btn-sm" data-expense-void="${esc(e.id)}">${esc(t("bk.void"))}</button></td></tr>`,
            ),
          )) +
      heldSection() +
      accountsSection() +
      buildingSection(),
    wire: (root, go) => {
      root.querySelector("#exportMonth")?.addEventListener("click", (event) => {
        void act(pressed(event), async () => {
          const entries = await data.monthLedger(session.orgId, store.period);
          save(
            excelFileName(store.orgName, store.period),
            toExcel(
              statement({
                period: store.period,
                orgName: store.orgName,
                by: store.people.get(session.user.uid) ?? t("role.accountant"),
                on: new Date(),
                entries,
                expenses: store.expenses,
                units: store.units,
                months: store.months,
                held: store.held.reduce((sum, row) => sum + row.amount, 0),
                people: store.people,
              }),
            ),
            "application/vnd.ms-excel;charset=utf-8",
          );
          toast(t("statement.done"));
        });
      });
      root.querySelector("#addExp")?.addEventListener("click", (event) => {
        const amount = Number(root.querySelector<HTMLInputElement>("#expAmt")?.value ?? "");
        const reason = root.querySelector<HTMLInputElement>("#expDesc")?.value.trim() ?? "";
        const vendor = root.querySelector<HTMLInputElement>("#expVendor")?.value.trim() ?? "";
        if (!Number.isInteger(amount) || amount < 1) {
          toast(t("books.needAmount"));
          return;
        }
        if (reason === "") {
          toast(t("books.needDescription"));
          return;
        }
        // Checked here as well as in the function, so an expense missing the
        // payee is said in Amharic on the form rather than in English from a
        // callable that turned it down.
        if (vendor === "") {
          toast(t("books.needVendor"));
          return;
        }
        void act(pressed(event), async () => {
          await data.recordExpense(session.orgId, {
            amount,
            category: expenseCategory(root.querySelector<HTMLSelectElement>("#expCat")?.value),
            reason,
            vendor,
          });
          toast(t("books.recorded"));
          go("books");
        });
      });
      const asked = (question: string): string | null => {
        const answer = window.prompt(question);
        if (answer === null) return null;
        if (answer.trim() === "") {
          toast(t("queue.needWhy"));
          return null;
        }
        return answer.trim();
      };
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-expense-void]")) {
        button.addEventListener("click", () => {
          const reason = asked(t("fix.why"));
          if (reason === null) return;
          void act(button, async () => {
            await data.voidExpense(session.orgId, {
              expenseId: button.dataset.expenseVoid ?? "",
              reason,
            });
            toast(t("bk.voided"));
          });
        });
      }
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-held-settle]")) {
        button.addEventListener("click", () => {
          const heldId = button.dataset.heldSettle ?? "";
          const choice = root.querySelector<HTMLSelectElement>(
            `[data-held-choice="${CSS.escape(heldId)}"]`,
          )?.value;
          const resolution = (["extended", "refunded", "waived"] as const).find(
            (r) => r === choice,
          );
          if (!resolution) return;
          const reason = asked(`${t(`held.${resolution}`)}. ${t("held.whyHint")}`);
          if (reason === null) return;
          void act(button, async () => {
            await data.resolveHeldFunds(session.orgId, { heldId, resolution, reason });
            toast(t("fix.done"));
          });
        });
      }
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-account-recount]")) {
        button.addEventListener("click", () => {
          const account = store.accounts.find((a) => a.id === button.dataset.accountRecount);
          if (!account) return;
          const counted = window.prompt(
            `${account.name}: ${t("bk.balance")}`,
            String(account.balance),
          );
          if (counted === null) return;
          const balance = Number(counted.trim());
          if (!Number.isSafeInteger(balance) || balance < 0) {
            toast(t("fix.needAmount"));
            return;
          }
          const reason = asked(t("fix.why"));
          if (reason === null) return;
          void act(button, async () => {
            await data.setAccountBalance(session.orgId, { accountId: account.id, balance, reason });
            toast(t("fix.done"));
          });
        });
      }
      root.querySelector("#acctAdd")?.addEventListener("click", (event) => {
        const name = root.querySelector<HTMLInputElement>("#acctName")?.value.trim() ?? "";
        const balance = Number(root.querySelector<HTMLInputElement>("#acctBalance")?.value ?? "");
        const reason = root.querySelector<HTMLInputElement>("#acctWhy")?.value.trim() ?? "";
        if (name === "" || !Number.isSafeInteger(balance) || balance < 0) {
          toast(t("fix.needAmount"));
          return;
        }
        if (reason === "") {
          toast(t("queue.needWhy"));
          return;
        }
        // An account's id is its name, lowercased, so recounting finds the same one.
        const accountId =
          name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "") || "account";
        void act(pressed(event), async () => {
          await data.setAccountBalance(session.orgId, { accountId, balance, name, reason });
          toast(t("fix.done"));
          go("books");
        });
      });
      root.querySelector("#bldSave")?.addEventListener("click", (event) => {
        const defaultMonthlyDue = Number(
          root.querySelector<HTMLInputElement>("#bldDue")?.value ?? "",
        );
        const dueDayOfMonth = Number(root.querySelector<HTMLInputElement>("#bldDay")?.value ?? "");
        const reason = root.querySelector<HTMLInputElement>("#bldWhy")?.value.trim() ?? "";
        if (!Number.isSafeInteger(defaultMonthlyDue) || defaultMonthlyDue < 1) {
          toast(t("fix.needAmount"));
          return;
        }
        if (reason === "") {
          toast(t("queue.needWhy"));
          return;
        }
        if (!session.buildingId) return;
        const buildingId = session.buildingId;
        void act(pressed(event), async () => {
          const reminders = root.querySelector<HTMLInputElement>("#bldReminders");
          await data.changeBuilding(session.orgId, {
            buildingId,
            defaultMonthlyDue,
            dueDayOfMonth,
            ...(reminders ? { autoReminders: reminders.checked } : {}),
            reason,
          });
          toast(t("fix.done"));
        });
      });
    },
  };
}

/** Money paid beyond what a unit owed, waiting for the accountant to place it. */
function heldSection(): string {
  return (
    sectionTitle(t("held.heading")) +
    (store.held.length === 0
      ? empty(t("held.none"))
      : card(
          rowList(
            store.held.map(
              (h) =>
                `<div class="row"><div class="left"><strong>${esc(money(h.amount))}</strong><span>${esc(h.unitId)}</span></div>` +
                `<div><select class="field" data-held-choice="${esc(h.id)}">${(
                  ["extended", "refunded", "waived"] as const
                )
                  .map((r) => `<option value="${r}">${esc(t(`held.${r}`))}</option>`)
                  .join(
                    "",
                  )}</select> <button type="button" class="btn btn-gold btn-sm" data-held-settle="${esc(h.id)}">${esc(
                  t("held.settle"),
                )}</button></div></div>`,
            ),
          ),
        ))
  );
}

/** What each account holds, as last counted, and a way to count again or add one. */
function accountsSection(): string {
  return (
    sectionTitle(t("bk.accounts")) +
    card(
      (store.accounts.length === 0
        ? `<p class="hint">${esc(t("bk.noAccounts"))}</p>`
        : rowList(
            store.accounts.map(
              (a) =>
                `<div class="row"><div class="left"><strong>${esc(a.name)}</strong><span>${esc(
                  a.countedAt ? t("bk.countedOn", { date: on(a.countedAt) }) : t("bk.neverCounted"),
                )}</span></div><div><strong>${esc(money(a.balance))}</strong> <button type="button" class="btn btn-ghost btn-sm" data-account-recount="${esc(
                  a.id,
                )}">${esc(t("bk.recount"))}</button></div></div>`,
            ),
          )) +
        `<h5>${esc(t("bk.addAccount"))}</h5><div class="form-grid form-grid-two">` +
        field("acctName", t("bk.accountName")) +
        field("acctBalance", t("bk.balance"), 'type="number" min="0" step="1"') +
        field("acctWhy", t("fix.why"), `placeholder="${esc(t("fix.whyHint"))}"`) +
        `</div><div class="actions">${gold("acctAdd", t("bk.addAccount"))}</div>`,
    )
  );
}

/** What the building bills a new unit, and the day every month falls due. */
function buildingSection(): string {
  const building = store.building;
  if (!building) {
    return "";
  }
  return (
    sectionTitle(t("bk.building")) +
    card(
      `<p class="hint">${esc(t("bk.buildingHint"))}</p><div class="form-grid form-grid-two">` +
        field(
          "bldDue",
          t("bk.defaultDue"),
          `type="number" min="1" step="1" value="${esc(String(building.defaultMonthlyDue))}"`,
        ) +
        field(
          "bldDay",
          t("bk.dueDay"),
          `type="number" min="1" max="30" step="1" value="${esc(String(building.dueDayOfMonth))}"`,
        ) +
        (offers("sendReminders", "desktop", "accountant", store.entitlements)
          ? `<div><label><input type="checkbox" id="bldReminders"${
              building.autoReminders ? " checked" : ""
            } /> ${esc(t("bk.autoReminders"))}</label><p class="hint">${esc(
              t("bk.autoRemindersHint", {
                days: AUTO_REMINDER_DELAY_DAYS,
                next: on(nextReminder(building.dueDayOfMonth)),
              }),
            )}</p></div>`
          : "") +
        field("bldWhy", t("fix.why"), `placeholder="${esc(t("fix.whyHint"))}"`) +
        `</div><div class="actions">${gold("bldSave", t("bk.changeBuilding"))}</div>`,
    )
  );
}

/** The next day this building's automatic reminder would go out. */
function nextReminder(dueDayOfMonth: number): Date {
  const now = new Date();
  const period = periodOf(now);
  for (const candidate of [addPeriods(period, -1), period, addPeriods(period, 1)]) {
    const when = reminderDateOf(candidate, dueDayOfMonth);
    if (when.getTime() + 86_400_000 > now.getTime()) return when;
  }
  return reminderDateOf(addPeriods(period, 1), dueDayOfMonth);
}

function arrearsTable(session: Session): View {
  const owing = behind();
  const canRemind = offers("sendReminders", "desktop", "accountant", store.entitlements);
  const worth = store.waiting.reduce((sum, p) => sum + p.amount, 0);
  return {
    title: t("nav.arrears"),
    sub: store.orgName,
    html:
      grid(3, [
        stat(t("arrears.behind"), String(owing.rows.length), ""),
        stat(t("arrears.total"), money(owing.owed), ""),
        stat(t("arrears.inQueue"), money(worth), t("arrears.inQueueSub")),
      ]) +
      (canRemind && owing.rows.length > 0
        ? card(
            `<h3>${esc(t("arrears.remindTitle"))}</h3><p class="hint">${esc(t("arrears.remindExplain"))}</p>` +
              `<div class="form-grid"><div><label for="remindNote">${esc(t("arrears.note"))}</label>` +
              `<textarea class="field" id="remindNote" rows="2" maxlength="${REMINDER_NOTE_LIMIT}" placeholder="${esc(
                t("arrears.noteHint"),
              )}"></textarea></div></div>` +
              `<div class="actions"><button type="button" class="btn btn-gold" id="remindAll">${esc(
                t("arrears.remindAll", { count: owing.rows.length }),
              )}</button></div>`,
          )
        : "") +
      sectionTitle(t("arrears.worstFirst")) +
      (owing.rows.length === 0
        ? empty(t("arrears.none"))
        : table(
            [t("queue.unit"), t("units.blockColumn"), t("queue.months"), t("units.balance"), ""],
            owing.rows.map(
              (r) =>
                `<tr><td><strong>${esc(r.unitId)}</strong></td><td>${esc(r.block)}</td>` +
                `<td>${esc(String(r.months))}</td><td>${esc(money(r.owed))}</td>` +
                `<td>${
                  canRemind
                    ? `<button type="button" class="btn btn-ghost btn-sm" data-remind="${esc(
                        r.unitId,
                      )}">${esc(t("arrears.remind"))}</button>`
                    : ""
                }</td></tr>`,
            ),
          )),
    wire: (root) => {
      // The message box above the table goes with every reminder sent from
      // this page, the one-unit buttons included.
      const note = () => root.querySelector<HTMLTextAreaElement>("#remindNote")?.value.trim() ?? "";
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-remind]")) {
        button.addEventListener("click", () => {
          const unitId = button.dataset.remind ?? "";
          void act(button, async () => {
            await data.remindUnits(session.orgId, { unitIds: [unitId], note: note() });
            toast(t("arrears.reminded"));
          });
        });
      }
      root.querySelector<HTMLButtonElement>("#remindAll")?.addEventListener("click", (event) => {
        const unitIds = owing.rows.map((r) => r.unitId);
        if (!window.confirm(t("arrears.confirmAll", { count: unitIds.length }))) return;
        void act(pressed(event), async () => {
          for (let start = 0; start < unitIds.length; start += REMINDER_LIMIT) {
            await data.remindUnits(session.orgId, {
              unitIds: unitIds.slice(start, start + REMINDER_LIMIT),
              note: note(),
            });
          }
          toast(t("arrears.remindedAll", { count: unitIds.length }));
        });
      });
    },
  };
}

function journal(): View {
  return {
    title: t("nav.journal"),
    sub: store.orgName,
    html:
      store.changes.length === 0
        ? empty()
        : card(
            rowList(
              store.changes.map((c) =>
                row(
                  named("action", c.action),
                  [
                    on(c.at),
                    c.by === null
                      ? null
                      : c.by.startsWith("system:")
                        ? t("journal.automatic")
                        : (store.people.get(c.by) ?? null),
                    c.reason,
                  ]
                    .filter((part) => part)
                    .join(" · "),
                  "",
                ),
              ),
            ),
          ),
  };
}

/* --------------------------------------------------------------- chapa -- */

/** A moment a person can read, in Addis Ababa's time whatever the computer's clock says. */
function atTime(date: Date): string {
  return date.toLocaleString(language() === "am" ? "am-ET" : "en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Africa/Addis_Ababa",
  });
}

const CHAPA_STATE = {
  verified: { css: "pill-verified", label: "chapa.verified" },
  submitted: { css: "pill-unverified", label: "chapa.submitted" },
  rejected: { css: "pill-unpaid", label: "chapa.rejected" },
} as const satisfies Record<string, { css: string; label: Key }>;

/**
 * What came in through Chapa: the accountant's receipts and the committee's
 * money. Both read the same payments, which only the function that asked Chapa
 * writes. Chapa pays the committee's bank about a day after each payment, so
 * the figures say on its way or in the bank, worked out by core.
 */
/**
 * The receipt the accountant has open, kept here rather than only in the page
 * so a redraw while it is open (a payment arriving, say) does not close it.
 */
let openReceipt: { paymentId: string; detail: ChapaReceiptDetail | null } | null = null;

function receiptRow(label: string, value: string): string {
  return `<div class="split"><span>${esc(label)}</span><strong>${esc(value)}</strong></div>`;
}

/** One Chapa payment's receipt: Chapa's word, asked just now, beside Liyu's record. */
function chapaReceiptCard(): string {
  if (!openReceipt) return "";
  const close = `<div class="actions"><button type="button" class="btn btn-ghost btn-sm" id="chapaReceiptClose">${esc(
    t("chapa.receiptClose"),
  )}</button></div>`;
  const detail = openReceipt.detail;
  if (!detail) {
    return card(
      `<h3>${esc(t("chapa.receiptTitle"))}</h3><p class="hint">${esc(t("chapa.receiptLoading"))}</p>${close}`,
    );
  }
  const { chapa } = detail;
  const paidAt = chapa.paidAt ? new Date(chapa.paidAt) : null;
  const covers = [
    ...detail.settledPeriods.map((period) => month(period as Period)),
    ...(detail.heldAmount > 0
      ? [t("chapa.receiptHeld", { amount: money(detail.heldAmount) })]
      : []),
  ].join(", ");
  const said = chapa.matches
    ? t("chapa.receiptConfirmed")
    : t("chapa.receiptMismatch", {
        status: chapa.status ?? "—",
        amount: chapa.amount === null ? "—" : money(chapa.amount),
      });
  const payer = [chapa.payerName, chapa.payerPhone].filter((part) => part !== null).join(" · ");
  return card(
    `<h3>${esc(t("chapa.receiptTitle"))}</h3><p class="hint">${esc(store.orgName)}</p>` +
      receiptRow(
        t("queue.unit"),
        detail.block
          ? [detail.unitId, t("units.block", { block: detail.block })].join(" · ")
          : detail.unitId,
      ) +
      receiptRow(t("queue.amount"), money(detail.amount)) +
      receiptRow(t("chapa.paidAt"), paidAt ? atTime(paidAt) : "—") +
      receiptRow(t("chapa.receiptMethod"), chapa.method ?? "—") +
      receiptRow(t("chapa.receiptPayer"), payer === "" ? "—" : payer) +
      receiptRow(t("chapa.receiptCovers"), covers === "" ? "—" : covers) +
      receiptRow(t("chapa.receiptChapaRef"), chapa.reference ?? "—") +
      receiptRow(t("chapa.receiptLiyuRef"), detail.orderId) +
      receiptRow(t("chapa.receiptChapaSays"), said) +
      `<p class="hint">${esc(t("chapa.receiptChecked", { when: atTime(new Date(detail.checkedAt)) }))}</p>` +
      close,
  );
}

function chapaView(role: "accountant" | "committee", session: Session): View {
  const now = new Date();
  const opens = role === "accountant";
  const receipts = store.chapaReceipts;
  const sums = chapaMoney(receipts, now);
  const title = role === "accountant" ? t("chapa.receipts") : t("chapa.money");
  const lead = role === "accountant" ? t("chapa.receiptsLead") : t("chapa.moneyLead");
  return {
    title,
    sub: store.orgName,
    html:
      `<p class="hint">${esc(lead)}</p>` +
      grid(3, [
        stat(
          t("chapa.onTheWay"),
          money(sums.onTheWay),
          sums.lastArrival
            ? `${t("chapa.count", { count: sums.onTheWayCount })} · ${t("chapa.lastArrival", {
                when: atTime(sums.lastArrival),
              })}`
            : t("chapa.count", { count: sums.onTheWayCount }),
        ),
        stat(
          t("chapa.reached"),
          money(sums.reached),
          t("chapa.count", { count: sums.reachedCount }),
        ),
        stat(t("chapa.total"), money(sums.total), t("chapa.count", { count: receipts.length })),
      ]) +
      `<div id="chapaReceipt">${opens ? chapaReceiptCard() : ""}</div>` +
      sectionTitle(t("chapa.receipts")) +
      (receipts.length === 0
        ? empty(t("chapa.none"))
        : table(
            [
              t("chapa.paidAt"),
              t("queue.unit"),
              t("queue.amount"),
              t("queue.reference"),
              t("chapa.status"),
              t("chapa.where"),
              ...(opens ? [t("chapa.receiptTitle")] : []),
            ],
            receipts.map((receipt) => {
              const state = CHAPA_STATE[receipt.status];
              const arrival = receipt.paidAt ? reachesBankBy(receipt.paidAt) : null;
              const where =
                arrival && arrival.getTime() <= now.getTime()
                  ? t("chapa.inBank")
                  : arrival
                    ? t("chapa.arrivesBy", { when: atTime(arrival) })
                    : t("chapa.onTheWay");
              return (
                `<tr><td>${esc(receipt.paidAt ? atTime(receipt.paidAt) : "—")}</td>` +
                `<td>${esc(receipt.unitId)}</td><td>${esc(money(receipt.amount))}</td>` +
                `<td class="mono">${esc(receipt.reference)}</td>` +
                `<td><span class="pill ${state.css}">${esc(t(state.label))}</span></td>` +
                `<td>${esc(where)}</td>` +
                (opens
                  ? `<td><button type="button" class="btn btn-ghost btn-sm" data-chapa-receipt="${esc(
                      receipt.paymentId,
                    )}">${esc(t("chapa.receiptOpen"))}</button></td>`
                  : "") +
                `</tr>`
              );
            }),
          )),
    wire: (root) => {
      if (!opens) return;
      const holder = root.querySelector<HTMLElement>("#chapaReceipt");
      const show = (): void => {
        if (holder) holder.innerHTML = chapaReceiptCard();
        holder?.querySelector("#chapaReceiptClose")?.addEventListener("click", () => {
          openReceipt = null;
          show();
        });
      };
      show();
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-chapa-receipt]")) {
        button.addEventListener("click", (event) => {
          const paymentId = button.dataset.chapaReceipt ?? "";
          openReceipt = { paymentId, detail: null };
          show();
          holder?.scrollIntoView({ behavior: "smooth", block: "start" });
          void act(pressed(event), async () => {
            try {
              const detail = await data.chapaReceipt(session.orgId, { paymentId });
              if (openReceipt?.paymentId === paymentId) openReceipt = { paymentId, detail };
            } catch (cause) {
              if (openReceipt?.paymentId === paymentId) openReceipt = null;
              throw cause;
            } finally {
              show();
            }
          });
        });
      }
    },
  };
}

/* ----------------------------------------------------------- committee -- */

export function committee(view: string, session: Session): View {
  const metric = metricFromRoute(view);
  if (metric) return analyticsView(metric, totals(), behind(), store.expenses);
  if (view === "units") {
    return unitsTable(session);
  }
  if (view === "campaigns") {
    return campaignsView(session);
  }
  if (view === "repairs") {
    return committeeRepairs(session);
  }
  if (view === "advertisements") {
    return committeeAdvertisements(session);
  }
  if (view === "notices") {
    return committeeNotices(session);
  }
  if (view === "chat") {
    return chatView(session);
  }
  if (view === "journal") {
    return journal();
  }
  if (view === "chapa") {
    return chapaView("committee", session);
  }
  return overviewView();
}

function overviewView(): View {
  const sums = totals();
  const drawn = map();
  const openRepairs = store.complaints.filter((c) => !SETTLED.has(c.status)).length;
  const heldTotal = store.held.reduce((sum, h) => sum + h.amount, 0);
  const rate = sums.month.billed === 0 ? 0 : sums.month.collected / sums.month.billed;

  return {
    title: t("nav.overview"),
    sub: [month(store.period), percent(rate)].join(" · "),
    html:
      metricCards(sums) +
      `<div class="grid-2" style="margin-top:1.15rem">` +
      card(
        `<h3>${esc(t("overview.rate"))}</h3>` +
          `<div class="split"><span>${esc(t("overview.rateRow"))}</span><strong>${esc(
            percent(rate),
          )}</strong></div>` +
          bar(rate) +
          rowList([
            row(t("state.verified"), "", `<strong>${esc(String(drawn.counts.verified))}</strong>`),
            row(t("state.partial"), "", `<strong>${esc(String(drawn.counts.partial))}</strong>`),
            row(
              t("state.unverified"),
              "",
              `<strong>${esc(String(drawn.counts.unverified))}</strong>`,
            ),
            row(t("state.unpaid"), "", `<strong>${esc(String(drawn.counts.unpaid))}</strong>`),
          ]),
      ) +
      card(
        `<h3>${esc(t("overview.attention"))}</h3>` +
          row(
            t("overview.queueRow"),
            t("overview.queueSub"),
            `<strong>${esc(String(store.waiting.length))}</strong>`,
          ) +
          row(
            t("overview.repairsRow"),
            t("overview.repairsSub"),
            `<strong>${esc(String(openRepairs))}</strong>`,
          ) +
          row(
            t("overview.heldRow"),
            t("overview.heldSub"),
            `<strong>${esc(money(heldTotal))}</strong>`,
          ),
      ) +
      `</div>` +
      sectionTitle(t("overview.byBlock")) +
      card(
        sums.blocks
          .map(
            (block) =>
              `<div style="margin-bottom:0.85rem"><div class="split"><strong>${esc(
                t("units.block", { block: block.block }),
              )}</strong><span>${esc(
                t("overview.blockLine", {
                  rate: Math.round((block.billed === 0 ? 0 : block.collected / block.billed) * 100),
                  owed: money(block.outstanding),
                }),
              )}</span></div>` +
              bar(block.billed === 0 ? 0 : block.collected / block.billed) +
              `</div>`,
          )
          .join(""),
      ),
    wire: wireMetrics,
  };
}

function campaignsView(session: Session): View {
  return {
    title: t("nav.campaigns"),
    sub: store.orgName,
    html:
      card(
        `<h3>${esc(t("campaigns.start"))}</h3><div class="form-grid form-grid-two">` +
          field("campTitle", t("campaigns.what"), `placeholder="${esc(t("campaigns.whatHint"))}"`) +
          field("campTarget", t("campaigns.target"), 'type="number" min="1" step="1"') +
          `</div><div class="actions">${gold("startCamp", t("campaigns.begin"))}</div>`,
      ) +
      sectionTitle(t("campaigns.all")) +
      (store.campaigns.length === 0
        ? empty()
        : card(
            store.campaigns
              .map((camp) => {
                const share = camp.target === 0 ? 0 : camp.raised / camp.target;
                return (
                  `<div class="row" style="align-items:flex-start;flex-wrap:wrap">` +
                  `<div class="left" style="flex:1;min-width:12rem"><strong>${esc(
                    camp.title,
                  )}</strong><span>${esc(
                    t("resident.raisedOf", {
                      raised: money(camp.raised),
                      target: money(camp.target),
                    }),
                  )}</span>${bar(share)}</div>` +
                  (camp.status === "open"
                    ? `<button type="button" class="btn btn-ghost btn-sm" data-close="${esc(
                        camp.id,
                      )}">${esc(t("campaigns.close"))}</button>`
                    : pill("closed")) +
                  `</div>`
                );
              })
              .join(""),
          )),
    wire: (root, go) => {
      root.querySelector("#startCamp")?.addEventListener("click", (event) => {
        const title = root.querySelector<HTMLInputElement>("#campTitle")?.value.trim() ?? "";
        const target = Number(root.querySelector<HTMLInputElement>("#campTarget")?.value ?? "");
        if (title === "" || !Number.isInteger(target) || target < 1) {
          toast(t("campaigns.needBoth"));
          return;
        }
        void act(pressed(event), async () => {
          await data.createCampaign(session.orgId, { title, target });
          toast(t("campaigns.started"));
          go("campaigns");
        });
      });
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-close]")) {
        button.addEventListener("click", () => {
          const campaignId = button.dataset.close ?? "";
          const reason = window.prompt(t("campaigns.close"));
          if (reason === null || reason.trim() === "") {
            return;
          }
          void act(button, async () => {
            await data.closeCampaign(session.orgId, { campaignId, reason: reason.trim() });
            toast(t("campaigns.wasClosed"));
            go("campaigns");
          });
        });
      }
      void CAMPAIGN_STATUSES;
    },
  };
}

/** Repairs whose worker picker is open, and the trade each one is showing. */
const openPickers = new Map<string, string>();

/**
 * Once someone is on a repair, one button that opens the committee's
 * contractors for it, already narrowed to the trade the repair needs, each
 * one tap from a call, a Telegram or a WhatsApp message with the job written.
 * Closed, it is a single line, so a long board stays readable. Messages go
 * from the committee member's phone and cost Liyu nothing.
 */
function reachWorker(c: data.Complaint): string {
  if (store.advertisements.length === 0) return "";
  const repairId = c.id;
  const open = openPickers.get(repairId);
  const toggle = `<button type="button" class="btn btn-ghost btn-sm" data-find-worker="${esc(repairId)}" aria-expanded="${String(
    open !== undefined,
  )}">${esc(t(open === undefined ? "repairs.findWorker" : "repairs.hideWorkers"))}</button>`;
  if (open === undefined) {
    return `<div class="reach-worker">${toggle}</div>`;
  }
  const message = t("repairs.message", {
    org: store.orgName,
    unit: c.unitId ?? "",
    // The message adds its own full stop after the problem.
    what: c.description.trim().replace(/[.!?።\s]+$/u, ""),
  });
  const listed = listedTrades();
  const people = store.advertisements
    .filter((ad) => open === "all" || ad.trade === open)
    .sort((x, y) => x.businessName.localeCompare(y.businessName));
  const links = people.map((ad) => {
    const digits = ad.phone.replace(/[^0-9]/g, "");
    const call = `tel:${ad.phone.replace(/[^0-9+]/g, "")}`;
    const whatsapp = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
    // Telegram has no link that opens a chat by phone number and fills it in,
    // so this opens Telegram's share sheet with the job written; the committee
    // picks the worker's chat there.
    const telegram = `https://t.me/share/url?url=${encodeURIComponent(message)}`;
    return (
      `<span class="reach-one"><span class="reach-name">${esc(ad.businessName)} · ${esc(
        tradeName(ad.trade),
      )}</span>` +
      `<a class="btn btn-ghost btn-sm" href="${esc(call)}">${esc(t("repairs.call"))}</a>` +
      `<a class="btn btn-ghost btn-sm" href="${esc(telegram)}" target="_blank" rel="noopener">${esc(
        t("repairs.telegram"),
      )}</a>` +
      `<a class="btn btn-ghost btn-sm" href="${esc(whatsapp)}" target="_blank" rel="noopener">${esc(
        t("repairs.whatsapp"),
      )}</a></span>`
    );
  });
  const choices = [
    ["all", t("contractors.allTrades")] as const,
    ...listed.map((trade) => [trade, tradeName(trade)] as const),
  ]
    .map(
      ([key, label]) =>
        `<option value="${esc(key)}"${key === open ? " selected" : ""}>${esc(label)}</option>`,
    )
    .join("");
  return (
    `<div class="reach-worker">${toggle}<div class="worker-panel">` +
    `<label class="worker-trade"><span>${esc(t("ads.trade"))}</span><select class="field" data-worker-trade="${esc(
      repairId,
    )}">${choices}</select></label>` +
    (links.length === 0
      ? `<p class="hint">${esc(t("contractors.noneOfTrade"))}</p>`
      : links.join("")) +
    `</div></div>`
  );
}

function committeeRepairs(session: Session): View {
  const moveButton = (id: string, to: ComplaintStatus) =>
    `<button type="button" class="btn btn-ghost btn-sm" data-move="${esc(id)}" data-to="${esc(to)}">${esc(
      t(`repairs.move.${to as "assigned" | "attended" | "closed" | "reported"}`),
    )}</button>`;
  return {
    title: t("nav.repairs"),
    sub: store.orgName,
    html:
      store.complaints.length === 0
        ? empty()
        : card(
            rowList(
              store.complaints.map((c) => {
                const moves = movesFrom(c.status, "committee");
                const who = c.assignee
                  ? ` · ${t("repairs.assignedTo", {
                      name: c.assignee.name,
                      trade: c.assignee.trade,
                      phone: c.assignee.phone,
                    })}`
                  : "";
                return (
                  row(
                    c.description,
                    [c.unitId, on(c.at)].filter((part) => part !== null).join(" · ") + who,
                    pill(c.status) + " " + moves.map((to) => moveButton(c.id, to)).join(" "),
                  ) + (c.status === "assigned" ? reachWorker(c) : "")
                );
              }),
            ),
          ),
    wire: (root, go) => {
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-find-worker]")) {
        button.addEventListener("click", () => {
          const id = button.dataset.findWorker ?? "";
          const complaint = store.complaints.find((c) => c.id === id);
          if (openPickers.has(id)) {
            openPickers.delete(id);
          } else {
            // Opens on the trade the repair needs, when its category says one
            // and somebody of that trade is listed; on everyone otherwise.
            const wanted = complaint ? tradeForRepair(complaint.category) : null;
            openPickers.set(id, wanted && listedTrades().includes(wanted) ? wanted : "all");
          }
          go("repairs");
        });
      }
      for (const select of root.querySelectorAll<HTMLSelectElement>("[data-worker-trade]")) {
        select.addEventListener("change", () => {
          openPickers.set(select.dataset.workerTrade ?? "", select.value);
          go("repairs");
        });
      }
      const find = (id: string | undefined) => store.complaints.find((c) => c.id === id);
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-move]")) {
        button.addEventListener("click", (event) => {
          const complaint = find(button.dataset.move);
          const to = button.dataset.to as ComplaintStatus | undefined;
          if (!complaint || !to) return;
          void act(pressed(event), async () => {
            await data.moveComplaint(session.orgId, complaint, to, "committee", session.user.uid);
            toast(t("repairs.moved"));
          });
        });
      }
    },
  };
}

/** The committee's contractor listings: add, edit, remove. */
function committeeAdvertisements(session: Session): View {
  const tradeOptions = CONTRACTOR_TRADES.map(
    (trade) => `<option value="${esc(trade)}">${esc(tradeName(trade))}</option>`,
  ).join("");
  const form = (id: string) =>
    `<div class="form-grid form-grid-two">` +
    field(`${id}Name`, t("ads.businessName")) +
    `<div><label for="${esc(`${id}Trade`)}">${esc(t("ads.trade"))}</label><select class="field" id="${esc(
      `${id}Trade`,
    )}"><option value="">${esc(t("ads.chooseTrade"))}</option>${tradeOptions}</select></div>` +
    field(`${id}Phone`, t("ads.phone"), 'type="tel" placeholder="0911 234 567"') +
    field(`${id}Description`, t("ads.description")) +
    `</div>`;
  const read = (root: HTMLElement, id: string) => {
    const value = (suffix: string) =>
      root.querySelector<HTMLInputElement | HTMLSelectElement>(`#${CSS.escape(`${id}${suffix}`)}`)
        ?.value ?? "";
    const trade = CONTRACTOR_TRADES.find((known) => known === value("Trade"));
    return {
      businessName: value("Name"),
      trade,
      description: value("Description"),
      phone: value("Phone"),
    };
  };
  return {
    title: t("nav.advertisements"),
    sub: store.orgName,
    html:
      card(
        `<h3>${esc(t("ads.add"))}</h3><p class="hint">${esc(t("ads.explain"))}</p>` +
          form("adNew") +
          `<div class="actions">${gold("adAdd", t("ads.save"))}</div>`,
      ) +
      sectionTitle(t("nav.advertisements")) +
      contractorDirectory(
        (ad) =>
          `<div><button type="button" class="btn btn-ghost btn-sm" data-ad-edit="${esc(ad.id)}">${esc(t("ads.edit"))}</button> ` +
          `<button type="button" class="btn btn-ghost btn-sm" data-ad-remove="${esc(ad.id)}">${esc(t("ads.remove"))}</button></div>` +
          `<div class="hidden contractor-edit" data-panel="ad-${esc(ad.id)}">` +
          form(`ad-${ad.id}-`) +
          `<div class="actions">${gold(`adSave-${ad.id}`, t("ads.save"))}</div></div>`,
      ),
    wire: (root, go) => {
      wireContractorDirectory(root);
      const save = (event: Event, input: ReturnType<typeof read>, id?: string) => {
        const trade = input.trade;
        if (!trade) {
          toast(t("ads.needTrade"));
          return;
        }
        const listing = { ...input, trade };
        try {
          advertisementInput({ ...listing, service: tradeName(trade) });
        } catch {
          toast(t("ads.bad"));
          return;
        }
        void act(pressed(event), async () => {
          await data.saveAdvertisement(session.orgId, session.user.uid, listing, id);
          toast(t("ads.saved"));
          go("advertisements");
        });
      };
      root.querySelector("#adAdd")?.addEventListener("click", (event) => {
        save(event, read(root, "adNew"));
      });
      for (const ad of store.advertisements) {
        root
          .querySelector(`[data-ad-edit="${CSS.escape(ad.id)}"]`)
          ?.addEventListener("click", () => {
            const panel = root.querySelector(`[data-panel="ad-${CSS.escape(ad.id)}"]`);
            panel?.classList.toggle("hidden");
            const prefix = `ad-${ad.id}-`;
            const set = (suffix: string, value: string) => {
              const input = root.querySelector<HTMLInputElement>(
                `#${CSS.escape(`${prefix}${suffix}`)}`,
              );
              if (input && input.value === "") input.value = value;
            };
            set("Name", ad.businessName);
            set("Trade", ad.trade);
            set("Phone", ad.phone);
            set("Description", ad.description);
          });
        root
          .querySelector(`#${CSS.escape(`adSave-${ad.id}`)}`)
          ?.addEventListener("click", (event) => {
            save(event, read(root, `ad-${ad.id}-`), ad.id);
          });
        root
          .querySelector(`[data-ad-remove="${CSS.escape(ad.id)}"]`)
          ?.addEventListener("click", (event) => {
            if (!window.confirm(t("ads.remove"))) return;
            void act(pressed(event), async () => {
              await data.removeAdvertisement(session.orgId, ad.id);
              toast(t("ads.removed"));
            });
          });
      }
    },
  };
}

function committeeNotices(session: Session): View {
  return {
    title: t("nav.notices"),
    sub: store.orgName,
    html:
      card(
        `<h3>${esc(t("notices.publish"))}</h3><div class="form-grid">` +
          field("noteTitle", t("notices.noticeTitle"), 'maxlength="120"') +
          `<div><label for="noteBody">${esc(
            t("notices.message"),
          )}</label><textarea class="field" id="noteBody" rows="3" maxlength="2000"></textarea></div>` +
          `<div><label for="noteDays">${esc(t("notices.keepUp"))}</label><select class="field" id="noteDays">` +
          `<option value="">${esc(t("notices.untilRemoved"))}</option>` +
          [7, 30]
            .map(
              (days) =>
                `<option value="${String(days)}">${esc(t("notices.forDays", { count: days }))}</option>`,
            )
            .join("") +
          `</select></div>` +
          `<div><label><input type="checkbox" id="noteUrgent" /> ${esc(t("notices.urgent"))}</label>` +
          `<p class="hint">${esc(t("notices.urgentHint"))}</p></div>` +
          `</div><div class="actions">${gold("postNote", t("notices.post"))}</div>`,
      ) +
      sectionTitle(t("notices.published")) +
      noticeList(true),
    wire: (root, go) => {
      root.querySelector("#postNote")?.addEventListener("click", (event) => {
        const title = root.querySelector<HTMLInputElement>("#noteTitle")?.value.trim() ?? "";
        const body = root.querySelector<HTMLTextAreaElement>("#noteBody")?.value.trim() ?? "";
        const days = root.querySelector<HTMLSelectElement>("#noteDays")?.value ?? "";
        const urgent = root.querySelector<HTMLInputElement>("#noteUrgent")?.checked ?? false;
        if (title === "" || body === "") {
          toast(t("notices.needBoth"));
          return;
        }
        void act(pressed(event), async () => {
          await data.postNotice(session.orgId, {
            title,
            body,
            urgent,
            days: days === "" ? null : Number(days),
            postedBy: session.user.uid,
            buildingId: session.buildingId ?? null,
          });
          toast(t("notices.posted"));
          go("notices");
        });
      });
      for (const button of root.querySelectorAll<HTMLButtonElement>("[data-notice-remove]")) {
        button.addEventListener("click", () => {
          if (!window.confirm(t("notices.removeConfirm"))) return;
          void act(button, async () => {
            await data.removeNotice(session.orgId, button.dataset.noticeRemove ?? "");
          });
        });
      }
    },
  };
}

function noticeList(removable = false): string {
  if (store.notices.length === 0) {
    return empty();
  }
  return card(
    rowList(
      store.notices.map(
        (n) =>
          `<div class="row" style="align-items:flex-start"><div class="left"><strong>${
            n.urgent ? `<span class="pill pill-unpaid">${esc(t("notices.urgentMark"))}</span> ` : ""
          }${esc(n.title)}</strong><span>${esc(n.body)}</span><span style="margin-top:0.25rem">${esc(
            [on(n.postedAt), n.expiresAt ? t("notices.until", { date: on(n.expiresAt) }) : null]
              .filter((part) => part !== null)
              .join(" · "),
          )}</span></div>${
            removable
              ? `<div class="right"><button type="button" class="btn btn-ghost btn-sm" data-notice-remove="${esc(
                  n.id,
                )}">${esc(t("notices.remove"))}</button></div>`
              : ""
          }</div>`,
      ),
    ),
  );
}

/**
 * A write, with its refusal shown rather than lost, and its button held.
 *
 * Every one of these is somebody pressing a button that moves money or tells a
 * resident something. A promise that rejects into nothing leaves the button
 * looking like it worked, and a button that stays live while its callable is
 * in flight can be pressed again before the first one lands.
 *
 * The hold is taken synchronously, before `work` is called, so the press is
 * answered in the frame it happened in rather than whenever the server gets
 * round to it. It is released in `finally`, so a refusal gives the button back
 * rather than leaving it dead.
 */
async function act(button: HTMLButtonElement | null, work: () => Promise<void>): Promise<void> {
  const release = button === null ? () => undefined : hold(button);
  try {
    await work();
  } catch (cause) {
    // A refusal the contract names is said in this app's words and language;
    // anything else is the function's own sentence about the work.
    const reason = refusalReasonOf((cause as { details?: unknown } | null)?.details);
    const message = reason
      ? t(`refusal.${reason}`)
      : cause instanceof Error
        ? cause.message
        : String(cause);
    toast(`${t("common.problem")} ${message}`);
  } finally {
    release();
  }
}
